// LaunchPage.js — Framer Motion + UI UX Pro Max Edition
// ✅ All original functionality preserved (auth, video, supabase, theme, nav)
// ✅ Framer Motion: scroll parallax, stagger reveals, AnimatePresence modal/dropdown
// ✅ whileHover / whileTap on every interactive surface
// ✅ Hero title word-by-word animation + phone entrance sequence
// ✅ Floating UI snippets driven by motion (replaces CSS keyframes)
// ✅ Stats counter animate-on-view
// ✅ UI UX Pro Max: Motion-Driven Sports/Fitness aesthetic

import React, { useEffect, useRef, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useInView,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye, faEyeSlash, faBars, faArrowRight, faMapMarkerAlt,
  faUsers, faTrophy, faDumbbell, faChevronDown, faChevronRight,
  faCalendarCheck, faBullhorn, faUserPlus, faComments, faLayerGroup,
  faFlag, faLock, faRepeat, faRocket, faBuilding, faMedal,
  faHeartPulse, faArrowTrendUp, faNetworkWired, faPeopleGroup,
  faGlobe, faXmark, faBolt, faCheck, faTimes, faSun, faMoon,
  faDownload, faPlus, faRunning, faBicycle, faFire, faShoePrints,
  faChartLine, faCircle, faPlay, faStar,
  faLocationDot, faPersonRunning, faUtensils, faRoute,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook, faTwitter, faInstagram, faTiktok,
} from "@fortawesome/free-brands-svg-icons";

import { useAuth } from "../AuthContext";
import { supabase } from "../supabaseClient";
import { loadInstitutionPublicPricingHidden } from "../services/institutionDemoMode";

import appstore from "../assets/ios.png";
import playstore from "../assets/android.png";
import logo from "../assets/logo.png";

import video1 from "../assets/basketball1.mp4";
import video2 from "../assets/basketball2.mp4";
import video3 from "../assets/football3.mp4";
import video4 from "../assets/football1.mp4";
import video5 from "../assets/football2.mp4";
import video6 from "../assets/basketball7.mp4";
import video7 from "../assets/tennis.mp4";
import video8 from "../assets/football4.mp4";

import SplineCanvas from "./SplineModel";
import { toast } from "react-toastify";

const SHOW_EVENTS_PAGE = false;
const EMAIL_CONFIRM_REDIRECT_URL = "https://snowsproam.com/auth/callback";

const isEmailNotConfirmedError = message => {
  const normalized = String(message || "").toLowerCase();
  return normalized.includes("email not confirmed") || normalized.includes("confirm your email") || normalized.includes("not confirmed");
};

/* ─── THEME ─── */
const DARK = {
  mode: "dark", accent: "#2B7FFF", accentAlt: "#00D4FF",
  accentGrad: "linear-gradient(135deg,#2B7FFF 0%,#0A1D44 100%)",
  accentGlow: "rgba(43,127,255,0.45)", accentBgSubtle: "rgba(43,127,255,0.08)",
  accentBgLight: "rgba(43,127,255,0.14)", accentBorder: "rgba(43,127,255,0.22)",
  bg: "#080810", bgAlt: "#0D0D1A", bgCard: "#12121F", bgElevated: "#1A1A2E",
  border: "rgba(255,255,255,0.07)", borderMedium: "rgba(255,255,255,0.12)",
  shadowSm: "0 1px 3px rgba(0,0,0,0.4)", shadowMd: "0 4px 20px rgba(0,0,0,0.5)",
  shadowLg: "0 16px 60px rgba(0,0,0,0.6)", text: "#F0F0FF",
  textSecondary: "rgba(240,240,255,0.65)", textMuted: "rgba(240,240,255,0.35)",
  textOnAccent: "#FFFFFF", white: "#FFFFFF", success: "#22D3A5", danger: "#FF4D6A",
  heroGradV: "linear-gradient(180deg,rgba(8,8,16,0.05) 0%,rgba(8,8,16,0.25) 40%,rgba(8,8,16,0.88) 75%,rgba(8,8,16,1) 100%)",
  heroGradH: "linear-gradient(90deg,rgba(8,8,16,0.70) 0%,transparent 55%)",
  headerBg: "rgba(8,8,16,0.88)", scrollbarThumb: "rgba(255,255,255,0.10)",
  inputBg: "rgba(255,255,255,0.04)", inputBorder: "rgba(255,255,255,0.10)",
  inputText: "#F0F0FF", inputPlaceholder: "rgba(240,240,255,0.30)",
  footerBg: "linear-gradient(180deg,#080810 0%,#04040A 100%)",
  footerText: "rgba(255,255,255,0.50)", footerMuted: "rgba(255,255,255,0.22)",
  footerLink: "rgba(255,255,255,0.60)", footerBorder: "rgba(255,255,255,0.06)",
  tagBg: "rgba(255,255,255,0.05)", tagBorder: "rgba(255,255,255,0.10)",
  tagText: "rgba(240,240,255,0.55)",
};
const LIGHT = {
  mode: "light", accent: "#1A6FEB", accentAlt: "#00AACC",
  accentGrad: "linear-gradient(135deg,#1A6FEB 0%,#0A1D44 100%)",
  accentGlow: "rgba(26,111,235,0.25)", accentBgSubtle: "rgba(26,111,235,0.06)",
  accentBgLight: "rgba(26,111,235,0.10)", accentBorder: "rgba(26,111,235,0.18)",
  bg: "#F0F2F8", bgAlt: "#E8EBF4", bgCard: "#FFFFFF", bgElevated: "#FFFFFF",
  border: "rgba(10,29,68,0.08)", borderMedium: "rgba(10,29,68,0.14)",
  shadowSm: "0 1px 3px rgba(10,29,68,0.07)", shadowMd: "0 4px 20px rgba(10,29,68,0.09)",
  shadowLg: "0 16px 60px rgba(10,29,68,0.12)", text: "#0A0E1F",
  textSecondary: "#3A4560", textMuted: "#7A8AAA", textOnAccent: "#FFFFFF",
  white: "#FFFFFF", success: "#059669", danger: "#DC2626",
  heroGradV: "linear-gradient(180deg,rgba(10,14,31,0.05) 0%,rgba(10,14,31,0.22) 42%,rgba(10,14,31,0.85) 76%,rgba(10,14,31,1) 100%)",
  heroGradH: "linear-gradient(90deg,rgba(10,14,31,0.60) 0%,transparent 52%)",
  headerBg: "rgba(240,242,248,0.92)", scrollbarThumb: "rgba(10,29,68,0.12)",
  inputBg: "#F5F7FC", inputBorder: "rgba(10,29,68,0.14)", inputText: "#0A0E1F",
  inputPlaceholder: "rgba(10,29,68,0.32)",
  footerBg: "linear-gradient(180deg,#07091A 0%,#03040E 100%)",
  footerText: "rgba(255,255,255,0.50)", footerMuted: "rgba(255,255,255,0.22)",
  footerLink: "rgba(255,255,255,0.60)", footerBorder: "rgba(255,255,255,0.06)",
  tagBg: "rgba(26,111,235,0.07)", tagBorder: "rgba(26,111,235,0.14)", tagText: "#1A6FEB",
};

const ACTIVITIES = [
  { name: "Ciarán M.", action: "joined a Football session", location: "Dublin", time: "2m ago", sport: "⚽" },
  { name: "Sophie R.", action: "hit #1 on the Fitness leaderboard", location: "Cork", time: "5m ago", sport: "🏃" },
  { name: "Jake T.", action: "completed a workout group", location: "Galway", time: "8m ago", sport: "💪" },
  { name: "Aoife K.", action: "joined a Basketball session", location: "Limerick", time: "11m ago", sport: "🏀" },
  { name: "Rian D.", action: "registered for a tournament", location: "Belfast", time: "14m ago", sport: "🏆" },
  { name: "Emma W.", action: "logged 12,000 steps today", location: "Waterford", time: "17m ago", sport: "👟" },
  { name: "Conor B.", action: "joined a Padel session", location: "Dublin", time: "20m ago", sport: "🎾" },
  { name: "Niamh S.", action: "started a workout group", location: "Kilkenny", time: "23m ago", sport: "🔥" },
];

/* ─── FRAMER MOTION VARIANTS ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};
const fadeLeft = {
  hidden: { opacity: 0, x: -44 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};
const fadeRight = {
  hidden: { opacity: 0, x: 44 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const staggerItem = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } },
};
const cardHover = {
  rest: { y: 0, boxShadow: "0 4px 20px rgba(0,0,0,0.3)" },
  hover: { y: -6, boxShadow: "0 20px 60px rgba(43,127,255,0.18)", transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1] } },
};
const modalVariants = {
  hidden: { opacity: 0, scale: 0.94, y: 16 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] } },
  exit: { opacity: 0, scale: 0.96, y: 8, transition: { duration: 0.2 } },
};
const dropdownVariants = {
  hidden: { opacity: 0, scaleY: 0.9, y: -8, originY: 0 },
  visible: { opacity: 1, scaleY: 1, y: 0, transition: { duration: 0.22, ease: [0.16, 1, 0.3, 1] } },
  exit: { opacity: 0, scaleY: 0.92, y: -4, transition: { duration: 0.15 } },
};
const heroWordVariants = {
  hidden: { opacity: 0, y: 40, rotateX: -20 },
  visible: (i) => ({
    opacity: 1, y: 0, rotateX: 0,
    transition: { delay: i * 0.12, duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  }),
};
const floatY = {
  animate: {
    y: [0, -10, 0],
    transition: { duration: 4, repeat: Infinity, ease: "easeInOut" },
  },
};
const floatYR = {
  animate: {
    y: [0, -10, 0],
    rotate: [-3, -3, -3],
    transition: { duration: 5, repeat: Infinity, ease: "easeInOut" },
  },
};

/* ─── GLOBAL CSS (non-animation styles only — FM handles reveals) ─── */
const makeGlobalCSS = (C) => `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800;900&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=Space+Mono:wght@400;700&display=swap');
*,*::before,*::after{margin:0;padding:0;box-sizing:border-box;-webkit-font-smoothing:antialiased;}
html{scroll-behavior:smooth;}
body{font-family:'DM Sans',sans-serif;background:${C.bg};color:${C.text};overflow-x:hidden;transition:background 0.35s,color 0.35s;}
a{color:inherit;text-decoration:none;}
::selection{background:rgba(43,127,255,0.28);}
@media(pointer:fine){.cursor-glow{position:fixed;width:500px;height:500px;border-radius:50%;pointer-events:none;z-index:0;background:radial-gradient(circle,${C.mode === "dark" ? "rgba(43,127,255,0.06)" : "rgba(26,111,235,0.04)"} 0%,transparent 70%);transform:translate(-50%,-50%);transition:left 0.12s ease,top 0.12s ease;will-change:left,top;}}
.noise-overlay{position:fixed;inset:0;pointer-events:none;z-index:1;opacity:${C.mode === "dark" ? "0.025" : "0.015"};background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");background-size:200px 200px;}
.heading{font-family:'Sora',sans-serif;}
.mono{font-family:'Space Mono',monospace;}
::-webkit-scrollbar{width:4px;}
::-webkit-scrollbar-track{background:transparent;}
::-webkit-scrollbar-thumb{background:${C.scrollbarThumb};border-radius:99px;}
@keyframes marquee{0%{transform:translateX(0);}100%{transform:translateX(-50%);}}
@keyframes livePulse{0%,100%{opacity:1;transform:scale(1);}50%{opacity:0.5;transform:scale(0.85);}}
@keyframes pulseRing{0%{transform:scale(1);opacity:0.6;}100%{transform:scale(2.2);opacity:0;}}
@keyframes shimmer{0%{transform:translateX(-100%);}100%{transform:translateX(100%);}}
.marquee-track{display:flex;animation:marquee 32s linear infinite;width:max-content;}
.live-dot{animation:livePulse 1.8s ease-in-out infinite;}
.pulse-ring{position:absolute;inset:0;border-radius:50%;border:2px solid ${C.accent};animation:pulseRing 2s ease-out infinite;}
.faq-answer{display:grid;grid-template-rows:0fr;transition:grid-template-rows 0.35s cubic-bezier(0.16,1,0.3,1);}
.faq-answer.open{grid-template-rows:1fr;}
.faq-answer>div{overflow:hidden;}
.glass-card{background:${C.mode === "dark" ? "rgba(18,18,31,0.85)" : "rgba(255,255,255,0.80)"};backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border:1px solid ${C.border};border-radius:20px;}
.grad-text{background:linear-gradient(135deg,${C.accent} 0%,${C.accentAlt} 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
.grad-text-warm{background:linear-gradient(135deg,#FF6B35 0%,#FFB800 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
.kinetic-num{font-family:'Sora',sans-serif;font-weight:900;line-height:1;background:linear-gradient(135deg,${C.mode === "dark" ? "#fff" : "#0A1D44"} 30%,${C.accent} 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
.section-number{font-family:'Space Mono',monospace;font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:${C.accent};opacity:0.8;}
.theme-btn{transition:transform 0.2s ease;}
.theme-btn:hover{transform:rotate(20deg) scale(1.1);}
.hero-video{transition:opacity 0.55s ease;}
.feat-tab{transition:all 0.2s ease;cursor:pointer;border:none;background:transparent;}
.feat-tab:hover{background:${C.accentBgSubtle}!important;}
.mesh-bg-blue{background:radial-gradient(ellipse 60% 50% at 20% 50%,${C.mode === "dark" ? "rgba(43,127,255,0.09)" : "rgba(26,111,235,0.05)"} 0%,transparent 70%),radial-gradient(ellipse 40% 60% at 80% 30%,${C.mode === "dark" ? "rgba(0,212,255,0.06)" : "rgba(0,170,204,0.04)"} 0%,transparent 70%),${C.bg};}
.mobile-sticky-cta{position:fixed;bottom:0;left:0;right:0;z-index:900;padding:12px 16px;display:none;gap:10px;background:${C.mode === "dark" ? "rgba(8,8,16,0.96)" : "rgba(240,242,248,0.96)"};border-top:1px solid ${C.border};backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);}
@media(max-width:768px){.mobile-sticky-cta{display:flex;}.hide-mobile{display:none!important;}}
@media(min-width:769px){.hide-desktop{display:none!important;}}
@media(max-width:640px){.scroll-x{display:flex;overflow-x:auto;scroll-snap-type:x mandatory;-webkit-overflow-scrolling:touch;gap:10px;padding-bottom:8px;scrollbar-width:none;}.scroll-x::-webkit-scrollbar{display:none;}.scroll-x>*{scroll-snap-align:start;flex:0 0 80%;min-width:260px;}}
.step-grid{position:relative;}
.step-grid::before{content:'';position:absolute;top:36px;left:36px;right:36px;height:2px;background:linear-gradient(90deg,${C.accent}44,${C.accentAlt}44);z-index:0;}
@media(max-width:768px){.step-grid::before{display:none;}}
.underline-bar{display:inline-block;height:4px;border-radius:2px;background:linear-gradient(90deg,${C.accent},${C.accentAlt});margin-top:10px;}
.dropdown-menu{position:absolute;top:42px;right:0;left:auto;width:230px;max-height:80vh;overflow-y:auto;background:${C.bgCard};border:1px solid ${C.border};border-radius:14px;box-shadow:${C.shadowLg};z-index:1001;}
@media(max-width:768px){.dropdown-menu{position:absolute;top:42px;right:0;left:auto;width:min(280px, calc(100vw - 24px));max-height:calc(100vh - 80px);overflow-y:auto;border-radius:16px;box-shadow:0 20px 60px rgba(0,0,0,0.5);}}
@media(max-width:768px){.hero-content-grid{grid-template-columns:1fr!important;}.hero-phone-col{display:none!important;}}
@media(max-width:900px){.story-grid{grid-template-columns:1fr!important;}.story-phone{display:none!important;}}
@media(max-width:640px){.feat-grid-2{grid-template-columns:1fr!important;}.stats-grid{grid-template-columns:1fr 1fr!important;}.footer-grid{grid-template-columns:1fr!important;}.orgs-grid{grid-template-columns:1fr!important;}.companies-grid{grid-template-columns:1fr!important;}.profile-cards{grid-template-columns:1fr 1fr!important;}.schools-resources{grid-template-columns:1fr 1fr!important;}}
`;

/* ─── ANIMATED COUNTER ─── */
const AnimatedCounter = ({ value, suffix = "", style: s = {} }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const isNum = !isNaN(parseInt(value));
    if (!isNum) { setDisplay(value); return; }
    const target = parseInt(value);
    let start = 0;
    const dur = 1400;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / dur, 1);
      const ease = 1 - Math.pow(1 - progress, 4);
      setDisplay(Math.floor(ease * target));
      if (progress < 1) requestAnimationFrame(step);
      else setDisplay(value);
    };
    requestAnimationFrame(step);
  }, [inView, value]);

  return (
    <span ref={ref} style={s}>{display}{suffix}</span>
  );
};

/* ─── MAIN COMPONENT ─── */
const LaunchPage = () => {
  const navigate = useNavigate();
  const { user, profile, accountType } = useAuth();

  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem("spa-theme") !== "light"; } catch { return true; }
  });
  const C = isDark ? DARK : LIGHT;
  const toggleTheme = useCallback(() => {
    setIsDark(v => { const n = !v; try { localStorage.setItem("spa-theme", n ? "dark" : "light"); } catch {} return n; });
  }, []);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [institutionPublicPricingHidden, setInstitutionPublicPricingHidden] = useState(true);
  useEffect(() => {
    let active = true;
    loadInstitutionPublicPricingHidden().then((hidden) => {
      if (active) setInstitutionPublicPricingHidden(hidden);
    }).catch(() => {
      if (active) setInstitutionPublicPricingHidden(true);
    });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const videoOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0.4]);
  const heroContentY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const videoRef = useRef(null);
  const currentVideoIndex = useRef(0);
  const dropdownRef = useRef(null);
  const burgerRef = useRef(null);
  const cursorGlowRef = useRef(null);

  const [isFading, setIsFading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [headerSolid, setHeaderSolid] = useState(false);
  const [openFaq, setOpenFaq] = useState(-1);
  const [activityIndex, setActivityIndex] = useState(0);
  const [showMobileCtaDismissed, setShowMobileCtaDismissed] = useState(false);

  const [signInData, setSignInData] = useState({ email: "", password: "" });
  const [unconfirmedSignIn, setUnconfirmedSignIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [address, setAddress] = useState("");
  const [instituteType, setInstituteType] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [error, setError] = useState("");
  const [groupSize, setGroupSize] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [password1Error, setPassword1Error] = useState(false);
  const [password2Error, setPassword2Error] = useState(false);
  const [businessNameError, setBusinessNameError] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [countryError, setCountryError] = useState(false);
  const [regionError, setRegionError] = useState(false);
  const [instituteTypeError, setInstituteTypeError] = useState(false);
  const [groupSizeError, setGroupSizeError] = useState(false);

  const [sports, setSports] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.from("sports").select("id,icon_url,team_limit").eq("active", true).order("id");
        if (data) {
          const seen = new Set();
          setSports(data.filter(s => { const k = s.id.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; }));
        }
      } finally { setSportsLoading(false); }
    })();
  }, []);

  const videoFiles = useMemo(() => [video1, video2, video8, video3, video6, video7, video4, video5], []);
  const validateEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const validatePassword = v => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/.test(v);
  const accountTypeResolved = accountType || profile?.account_type || user?.user_metadata?.account_type;
  const scrollTo = useCallback(id => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }); }, []);

  useEffect(() => {
    const h = () => setHeaderSolid(window.scrollY > 60);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    const h = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && burgerRef.current && !burgerRef.current.contains(e.target))
        setIsDropdownOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onEnd = () => {
      setIsFading(true);
      setTimeout(() => {
        currentVideoIndex.current = (currentVideoIndex.current + 1) % videoFiles.length;
        el.src = videoFiles[currentVideoIndex.current];
        el.currentTime = 0;
        el.play().catch(() => {});
        setIsFading(false);
      }, 500);
    };
    el.src = videoFiles[currentVideoIndex.current];
    el.play().catch(() => {});
    el.addEventListener("ended", onEnd);
    return () => el.removeEventListener("ended", onEnd);
  }, [videoFiles]);

  useEffect(() => {
    const id = setInterval(() => setActivityIndex(i => (i + 1) % ACTIVITIES.length), 3500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const el = cursorGlowRef.current;
    if (!el || isMobile) return;
    const h = e => { el.style.left = e.clientX + "px"; el.style.top = e.clientY + "px"; };
    document.addEventListener("mousemove", h);
    return () => document.removeEventListener("mousemove", h);
  }, [isMobile]);

  const handleForgotPassword = async () => {
    if (!signInData.email) { toast.error("Enter your email for a reset link."); return; }
    try {
      const resetUrl = `${window.location.origin}/reset-password`;
      const { error: e } = await supabase.auth.resetPasswordForEmail(signInData.email, { redirectTo: resetUrl });
      if (e) throw e;
      toast.success("Reset email sent. Check your inbox.");
    } catch (e) { toast.error(e?.message || "An error occurred."); }
  };

  const handleSignIn = async e => {
    e.preventDefault();
    setEmailError(!signInData.email.trim()); setPasswordError(!signInData.password.trim());
    if (!signInData.email.trim() || !signInData.password.trim()) { setError("Please fill in all fields."); toast.error("Please fill in all fields."); return; }
    setError("");
    try {
      const { error: err } = await supabase.auth.signInWithPassword({ email: signInData.email, password: signInData.password });
      if (err) {
        if (isEmailNotConfirmedError(err.message)) {
          setUnconfirmedSignIn(true);
          setError("Confirm your email before signing in. Check your inbox or resend the confirmation email.");
          toast.info("Confirm your email first. Your email and password are still here so you can try again after confirming.");
          return;
        }
        setUnconfirmedSignIn(false);
        setError(err.message); toast.error(err.message); return;
      }
      setUnconfirmedSignIn(false);
      setIsModalOpen(false); toast.success("Signed in!");
    } catch (err) { setError(err?.message || "Login failed."); toast.error(err?.message || "Login failed."); }
  };

  const handleResendConfirmation = async () => {
    if (!signInData.email.trim()) { toast.error("Enter your email to resend confirmation."); return; }
    try {
      const { error: err } = await supabase.auth.resend({
        type: "signup",
        email: signInData.email.trim(),
        options: { emailRedirectTo: EMAIL_CONFIRM_REDIRECT_URL },
      });
      if (err) throw err;
      toast.success("Confirmation email sent. Check your inbox, then try logging in again.");
    } catch (err) {
      toast.error(err?.message || "Could not resend confirmation email.");
    }
  };

  const handleSignup = async e => {
    e.preventDefault();
    setIsSignUp(false);
    setError("Business setup now starts from an existing Snows ProAm profile. Sign in first, then link business tools from the setup flow.");
    toast.info("Sign in with your profile first, then link business tools.");
  };

  const handleSignOut = async () => { try { await supabase.auth.signOut(); } catch (e) { console.error(e); } };
  const handleSignInChange = e => {
    const { name, value } = e.target;
    setSignInData(p => ({ ...p, [name]: value }));
    setUnconfirmedSignIn(false);
    if (name === "email") setEmailError(false);
    if (name === "password") setPasswordError(false);
  };

  const tag = () => ({ padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, background: C.tagBg, border: `1px solid ${C.tagBorder}`, color: C.tagText });

  const resources = [
    { title: "Student Pack", meta: "Quick start tips", href: "https://www.canva.com/design/DAGxlaVPVeU/42O9Qpm5DX4ndOoBZmKw7g/edit" },
    { title: "Printables", meta: "Year code sheet", href: "https://media.snowsproam.com/Schools/StepsChallenge-Ireland/YearCode.pdf" },
    { title: "Download", meta: "Get the app", href: "https://linktr.ee/snowsproam" },
  ];

  const faqs = [
    { q: "Is this only for serious athletes?", a: "Not at all. Snows ProAm is for anyone who wants to train, play, and show up — from beginners to seasoned athletes." },
    { q: "What's the fastest way to start?", a: "Head to Sessions, find one near you, and join. That's it. You'll be moving with real people in minutes." },
    { q: "How do communities work?", a: "Public communities are open to join. Private ones require a request, which keeps things organised and intentional." },
    { q: "Do I need a team?", a: "Nope. Join solo, meet people through sessions, and form workout groups naturally over time." },
    { q: "What does it cost?", a: institutionPublicPricingHidden
      ? "The app is free for individuals. Contact our team to discuss an institution setup."
      : "The app is free for individuals. Institutions have plans starting at €19/mo. All plans are currently free during our launch phase." },
    { q: "What sports are supported?", a: "Football, basketball, tennis, padel, fitness, running, cycling, and many more. Every sport has its own ecosystem." },
  ];

  /* ── Phone frame ── */
  const Phone = ({ children, rotate = -2, width = 220, style: s = {} }) => {
    const h = Math.round(width * 2);
    return (
      <div style={{
        width, height: h, borderRadius: 42, flexShrink: 0,
        background: isDark ? "#080810" : "#12121F",
        border: `7px solid ${isDark ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.22)"}`,
        overflow: "hidden", position: "relative",
        boxShadow: `0 40px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)`,
        transform: `rotate(${rotate}deg)`,
        ...s,
      }}>
        <div style={{ width: 80, height: 22, borderRadius: 11, background: "#000", margin: "8px auto", position: "relative", zIndex: 2, flexShrink: 0 }} />
        <div style={{ padding: "0 10px", height: "calc(100% - 38px)", overflow: "hidden" }}>{children}</div>
      </div>
    );
  };

  const activity = ACTIVITIES[activityIndex];
  const heroWords = ["Train.", "Play.", "Show up."];

  /* ═══════════ RENDER ═══════════ */
  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, transition: "background 0.35s,color 0.35s", position: "relative" }}>
      <style>{makeGlobalCSS(C)}</style>
      <div className="noise-overlay" />
      {!isMobile && <div ref={cursorGlowRef} className="cursor-glow" />}

      {/* ════ HEADER ════ */}
      <motion.header
        style={{
          position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000,
          padding: "0 16px", height: 60,
          display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: 8,
          background: headerSolid ? C.headerBg : "transparent",
          backdropFilter: headerSolid ? "blur(24px) saturate(1.3)" : "none",
          WebkitBackdropFilter: headerSolid ? "blur(24px) saturate(1.3)" : "none",
          borderBottom: headerSolid ? `1px solid ${C.border}` : "1px solid transparent",
          transition: "background 0.3s,border-color 0.3s",
        }}
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Logo */}
        <motion.div
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
        >
          <img src={logo} alt="Snows ProAm" style={{ height: 30 }} />
        </motion.div>

        {/* Desktop nav */}
        {!isMobile && (
          <nav style={{ display: "flex", alignItems: "center", gap: 2, overflowX: "auto", scrollbarWidth: "none", flex: 1, minWidth: 0, marginLeft: 8 }}>
            {[
              { label: "Sessions", action: () => navigate("/sessions") },
              { label: "Leaderboard", action: () => navigate("/leaderboard") },
              { label: "Institutes", action: () => navigate("/instituteleaderboard") },
              { label: "Live Matches", action: () => navigate("/livematches") },
              { label: "Venues", action: () => navigate("/venues") },
              { label: "Wellness", action: () => navigate("/wellness") },
              { label: "Coaching", action: () => navigate("/coaching") },
              { label: "Steps Worldwide", action: () => window.location.assign("https://worldwidesteps.com/") },
              { label: "Swimming", action: () => navigate("/sport-ireland/swimming") },
            ].map(n => (
              <motion.button
                key={n.label}
                onClick={n.action}
                style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  color: !headerSolid ? "rgba(255,255,255,0.78)" : isDark ? "rgba(255,255,255,0.78)" : "#0A1D44",
                  padding: "7px 12px", borderRadius: 8, fontSize: 13, fontWeight: 500,
                  fontFamily: "'DM Sans',sans-serif", whiteSpace: "nowrap", flexShrink: 0,
                }}
                whileHover={{ background: isDark ? "rgba(255,255,255,0.09)" : "rgba(26,111,235,0.08)", color: isDark ? "#fff" : C.accent }}
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.15 }}
              >{n.label}</motion.button>
            ))}
          </nav>
        )}

        {/* Right controls */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {!user && !isMobile && (
            <motion.button
              onClick={() => { setIsSignUp(false); setError(""); setIsModalOpen(true); }}
              style={{ background: C.accentGrad, border: "none", color: "#fff", padding: "8px 18px", borderRadius: 9, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "'Sora',sans-serif", whiteSpace: "nowrap" }}
              whileHover={{ y: -2, boxShadow: `0 10px 36px ${C.accentGlow}`, filter: "brightness(1.08)" }}
              whileTap={{ scale: 0.97 }}
            >Sign In</motion.button>
          )}
          {user && !isMobile && (
            <span style={{ fontSize: 12.5, color: C.textMuted, fontWeight: 500 }}>{profile?.email ?? user?.email ?? ""}</span>
          )}
          <motion.button
            className="theme-btn"
            onClick={toggleTheme}
            style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${headerSolid ? C.border : "rgba(255,255,255,0.18)"}`, background: headerSolid ? C.bgElevated : "rgba(255,255,255,0.10)", color: headerSolid ? C.text : "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
            whileHover={{ rotate: 20, scale: 1.1 }}
            whileTap={{ scale: 0.93 }}
          >
            <FontAwesomeIcon icon={isDark ? faSun : faMoon} style={{ fontSize: 13 }} />
          </motion.button>

        </div>
      </motion.header>

      {/* ════ HERO ════ */}
      <section ref={heroRef} style={{ position: "relative", width: "100%", height: isMobile ? "auto" : "100svh", minHeight: isMobile ? "100svh" : 700, display: "flex", alignItems: "flex-end", overflow: "hidden" }}>
        <motion.video
          ref={videoRef}
          muted
          playsInline
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover",
            opacity: isFading ? 0 : 1,
            scale: videoScale,
            transition: "opacity 0.55s ease",
          }}
        />
        <div style={{ position: "absolute", inset: 0, background: C.heroGradV }} />
        <div style={{ position: "absolute", inset: 0, background: C.heroGradH }} />

        {/* Hero content */}
        <motion.div
          style={{
            position: "relative", zIndex: 2, width: "100%", maxWidth: 1200, margin: "0 auto",
            padding: isMobile ? "120px 20px 100px" : "0 40px 80px",
            display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: isMobile ? 0 : 40, alignItems: "flex-end",
            y: heroContentY,
          }}
        >
          {/* Left copy */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              style={{ fontSize: isMobile ? 14 : 15, fontWeight: 600, color: "#D8E8FF", marginBottom: 20 }}
            >
              Where athletes belong
            </motion.p>

            <h1 className="heading" style={{ fontSize: isMobile ? 48 : 78, fontWeight: 900, lineHeight: 0.93, letterSpacing: -2, color: "#FFFFFF", marginBottom: 18, perspective: 800 }}>
              {heroWords.map((word, i) => (
                <React.Fragment key={word}>
                  <motion.span
                    custom={i}
                    variants={heroWordVariants}
                    initial="hidden"
                    animate="visible"
                    style={{
                      display: "block",
                      background: i === 2 ? `linear-gradient(135deg,${C.accent} 0%,#00D4FF 100%)` : "none",
                      WebkitBackgroundClip: i === 2 ? "text" : "none",
                      WebkitTextFillColor: i === 2 ? "transparent" : "#FFFFFF",
                      backgroundClip: i === 2 ? "text" : "none",
                    }}
                  >
                    {word}
                  </motion.span>
                </React.Fragment>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.6 }}
              style={{ fontSize: isMobile ? 16 : 18, lineHeight: 1.65, color: "rgba(255,255,255,0.78)", maxWidth: 460, marginBottom: 28 }}
            >
              The athletes near you are already on it. Find a session and join them.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.78, duration: 0.6 }}
              style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 32 }}
            >
              <motion.button
                onClick={() => navigate("/sessions")}
                style={{ background: `linear-gradient(135deg,${C.accent} 0%,#0056CC 100%)`, border: "none", color: "#fff", padding: isMobile ? "13px 22px" : "14px 28px", borderRadius: 12, cursor: "pointer", fontSize: isMobile ? 14 : 15, fontWeight: 700, fontFamily: "'Sora',sans-serif", display: "inline-flex", alignItems: "center", gap: 8 }}
                whileHover={{ y: -3, boxShadow: `0 12px 40px ${C.accentGlow}`, filter: "brightness(1.08)" }}
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.2 }}
              >
                Join a session <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12 }} />
              </motion.button>
              <motion.a
                href="#schools"
                style={{ background: "rgba(255,255,255,0.94)", border: "1px solid #fff", color: "#0A1D44", padding: isMobile ? "13px 18px" : "14px 22px", borderRadius: 12, fontSize: isMobile ? 14 : 15, fontWeight: 700, fontFamily: "'DM Sans',sans-serif", display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}
                whileHover={{ y: -3, background: "#fff" }}
                whileTap={{ scale: 0.97 }}
              >
                Schools <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12 }} />
              </motion.a>

              <motion.button
                onClick={() => window.open("https://linktr.ee/snowsproam", "_blank")}
                style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.22)", color: "#fff", padding: isMobile ? "13px 18px" : "14px 22px", borderRadius: 12, cursor: "pointer", fontSize: isMobile ? 14 : 15, fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}
                whileHover={{ y: -3, background: "rgba(255,255,255,0.16)" }}
                whileTap={{ scale: 0.97 }}
              >
                Download free
              </motion.button>
              <motion.button
                onClick={() => window.open("https://snowsproam.com/relayforlife", "_blank")}
                style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.35)", color: "#FFB800", padding: isMobile ? "13px 16px" : "14px 20px", borderRadius: 12, cursor: "pointer", fontSize: isMobile ? 13 : 14, fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}
                whileHover={{ y: -3, background: "rgba(245,158,11,0.22)" }}
                whileTap={{ scale: 0.97 }}
              >
                🎗️ Relay for Life
              </motion.button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.95, duration: 0.6 }}
              style={{ display: "flex", gap: isMobile ? 20 : 36, flexWrap: "wrap" }}
            >
              {[{ val: "Multi-sport", sub: "Sessions daily" }, { val: "Real-time", sub: "Leaderboards" }, { val: "Free", sub: "For athletes" }].map((s, i) => (
                <motion.div
                  key={s.sub}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.95 + i * 0.08 }}
                >
                  <div className="heading" style={{ fontSize: 17, fontWeight: 800, color: "#fff", letterSpacing: -0.3 }}>{s.val}</div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 2 }}>{s.sub}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Phone cluster */}
          <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-end", position: "relative", height: isMobile ? 320 : 480, marginTop: isMobile ? 40 : 0, overflow: isMobile ? "hidden" : "visible" }}>
            {/* Main phone */}
            <motion.div
              initial={{ opacity: 0, y: 60, rotate: -4 }}
              animate={{ opacity: 1, y: 0, rotate: -2 }}
              transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              style={{ width: isMobile ? 170 : 220, height: isMobile ? 340 : 440, borderRadius: isMobile ? 34 : 42, flexShrink: 0, background: "#080810", border: `${isMobile ? 5 : 7}px solid rgba(255,255,255,0.16)`, overflow: "hidden", position: "relative", boxShadow: "0 40px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)", zIndex: 5 }}
            >
              <div style={{ width: isMobile ? 60 : 80, height: isMobile ? 16 : 22, borderRadius: 11, background: "#000", margin: "8px auto", position: "relative", zIndex: 2 }} />
              <div style={{ padding: "4px 10px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.accent, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Sessions near you</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, padding: "7px 9px", borderRadius: 9, background: "rgba(43,127,255,0.12)", border: `1px solid ${C.accentBorder}` }}>
                  <span style={{ fontSize: 10, color: "#22D3A5", fontWeight: 700 }}>247 athletes active now</span>
                </div>
                {[
                  { sport: "⚽", name: "5-a-side Football", loc: "Dublin City", spots: 3, time: "Tonight 7pm" },
                  { sport: "🏀", name: "Basketball Pickup", loc: "Ballymun", spots: 5, time: "Today 6pm" },
                  { sport: "💪", name: "Fitness Group", loc: "City Centre", spots: 8, time: "Sat 9am" },
                ].map((s, i) => (
                  <div key={i} style={{ padding: "9px 9px", borderRadius: 9, marginBottom: 5, background: i === 0 ? C.accentBgSubtle : "rgba(255,255,255,0.03)", border: `1px solid ${i === 0 ? C.accentBorder : "rgba(255,255,255,0.07)"}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 13 }}>{s.sport}</span>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#F0F0FF" }}>{s.name}</div>
                        <div style={{ fontSize: 9, color: "rgba(240,240,255,0.45)", marginTop: 1 }}>{s.loc} · {s.time}</div>
                      </div>
                    </div>
                    <div style={{ marginTop: 5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 9, color: "#22D3A5", fontWeight: 600 }}>{s.spots} spots left</span>
                      {i === 0 && <div style={{ background: C.accentGrad, padding: "2px 8px", borderRadius: 5, fontSize: 9, fontWeight: 700, color: "#fff" }}>Join →</div>}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Secondary phones — desktop only */}
            {!isMobile && (
              <>
                <motion.div
                  initial={{ opacity: 0, x: -40, rotate: 3 }}
                  animate={{ opacity: 0.65, x: 0, rotate: 5 }}
                  transition={{ delay: 0.7, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                  style={{ position: "absolute", left: "50%", bottom: 20, transform: "translateX(-115%) rotate(5deg)", width: 190, height: 380, borderRadius: 38, zIndex: 3, background: "#0A0A18", border: "6px solid rgba(255,255,255,0.08)", overflow: "hidden", boxShadow: "0 32px 60px rgba(0,0,0,0.4)" }}
                >
                  <div style={{ width: 70, height: 18, borderRadius: 9, background: "#000", margin: "7px auto" }} />
                  <div style={{ padding: "4px 10px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#FFB800", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Leaderboard</div>
                    {[{ n: "Ciarán M.", pts: "12,840", pos: 1 }, { n: "Sophie R.", pts: "11,560", pos: 2 }, { n: "You", pts: "9,200", pos: 5, me: true }].map((e, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 8px", borderRadius: 8, marginBottom: 4, background: e.me ? "rgba(43,127,255,0.15)" : "rgba(255,255,255,0.03)", border: `1px solid ${e.me ? C.accentBorder : "rgba(255,255,255,0.06)"}` }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: i === 0 ? "#FFB800" : "rgba(240,240,255,0.4)", width: 14 }}>{e.pos}</span>
                        <span style={{ fontSize: 11, color: e.me ? C.accent : "#F0F0FF", fontWeight: e.me ? 700 : 500, flex: 1 }}>{e.n}</span>
                        <span style={{ fontSize: 10, color: "rgba(240,240,255,0.5)", fontFamily: "'Space Mono',monospace" }}>{e.pts}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 40, rotate: -4 }}
                  animate={{ opacity: 0.65, x: 0, rotate: -6 }}
                  transition={{ delay: 0.75, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                  style={{ position: "absolute", right: "50%", bottom: 20, transform: "translateX(115%) rotate(-6deg)", width: 190, height: 380, borderRadius: 38, zIndex: 3, background: "#0A0A18", border: "6px solid rgba(255,255,255,0.08)", overflow: "hidden", boxShadow: "0 32px 60px rgba(0,0,0,0.4)" }}
                >
                  <div style={{ width: 70, height: 18, borderRadius: 9, background: "#000", margin: "7px auto" }} />
                  <div style={{ padding: "4px 10px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#22D3A5", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Week 3</div>
                    <div style={{ padding: "12px 10px", borderRadius: 10, background: "rgba(34,211,165,0.08)", border: "1px solid rgba(34,211,165,0.2)", marginBottom: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#22D3A5" }}>+240 XP</div>
                      <div style={{ fontSize: 10, color: "rgba(240,240,255,0.5)", marginTop: 2 }}>Upper body complete</div>
                    </div>
                    {["Push-ups · 4×20", "Pull-ups · 3×10", "Dips · 3×15"].map((w, i) => (
                      <div key={i} style={{ padding: "8px 8px", borderRadius: 8, marginBottom: 4, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 10, color: "#F0F0FF", fontWeight: 500 }}>{w}</span>
                        <span style={{ fontSize: 12, color: "#22D3A5" }}>✓</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </>
            )}

            {/* Floating snippets — motion-driven */}
            <motion.div
              variants={floatY}
              animate="animate"
              style={{ position: "absolute", top: isMobile ? 10 : 20, right: isMobile ? -8 : -10, background: "rgba(34,211,165,0.15)", backdropFilter: "blur(20px)", border: "1px solid rgba(34,211,165,0.3)", borderRadius: 12, padding: isMobile ? "7px 10px" : "10px 14px", zIndex: 20 }}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: isMobile ? 13 : 16 }}>⚽</span>
                <div>
                  <div style={{ fontSize: isMobile ? 10 : 12, fontWeight: 700, color: "#22D3A5" }}>Session joined!</div>
                  <div style={{ fontSize: isMobile ? 9 : 10, color: "rgba(240,240,255,0.6)" }}>Tonight · 5-a-side</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              animate={{ y: [0, -10, 0], rotate: [-3, -3, -3] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              style={{ position: "absolute", bottom: isMobile ? 20 : 60, left: isMobile ? -8 : -20, background: "rgba(255,184,0,0.12)", backdropFilter: "blur(20px)", border: "1px solid rgba(255,184,0,0.25)", borderRadius: 12, padding: isMobile ? "7px 10px" : "10px 14px", zIndex: 20 }}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: isMobile ? 13 : 16 }}>🏆</span>
                <div>
                  <div style={{ fontSize: isMobile ? 10 : 12, fontWeight: 700, color: "#FFB800" }}>Rank up!</div>
                  <div style={{ fontSize: isMobile ? 9 : 10, color: "rgba(240,240,255,0.6)" }}>#3 Nationally</div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {!isMobile && (
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            style={{ position: "absolute", bottom: 28, left: "50%", transform: "translateX(-50%)", zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, opacity: 0.45, color: "#fff" }}
          >
            <span className="mono" style={{ fontSize: 10, letterSpacing: 2, textTransform: "uppercase" }}>Scroll</span>
            <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 12 }} />
          </motion.div>
        )}
      </section>

      {/* ════ VISION & MISSION ════ */}
      <section aria-label="Our vision and mission" style={{ padding: isMobile ? "64px 20px" : "88px 40px", background: C.bg }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 40 : 80 }}>
          <div>
            <h2 className="heading" style={{ fontSize: isMobile ? 28 : 36, fontWeight: 800, letterSpacing: -0.8, color: C.text, marginBottom: 16 }}>Our vision</h2>
            <p style={{ fontSize: isMobile ? 18 : 22, color: C.textSecondary, lineHeight: 1.55, maxWidth: 500 }}>To make sport and fitness accessible to everyone at every level.</p>
          </div>
          <div>
            <h2 className="heading" style={{ fontSize: isMobile ? 28 : 36, fontWeight: 800, letterSpacing: -0.8, color: C.text, marginBottom: 16 }}>Our mission</h2>
            <p style={{ fontSize: isMobile ? 18 : 22, color: C.textSecondary, lineHeight: 1.55, maxWidth: 500 }}>To connect professionals and amateurs with the community tools and opportunities to play, train, and compete.</p>
          </div>
        </div>
      </section>

      {/* ════ TICKER ════ */}
      <div style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, padding: "14px 0", background: isDark ? C.bgAlt : C.bgCard, overflow: "hidden" }}>
        <div className="marquee-track">
          {[...Array(2)].map((_, r) => (
            <React.Fragment key={r}>
              {["Find Sessions", "Leaderboards", "Live Matches", "Workout Groups", "Tournaments", "Steps Worldwide", "Communities", "Venues", "Corporate Wellness", "Coaching", ...(SHOW_EVENTS_PAGE ? ["Events"] : []), "Highlights", "Direct Chat", "Challenges", "Fitness Tracking", "ELO Matchmaking", "Mile Challenges", "Sport Forums", "Athlete Profiles"].map(t => (
                <span key={`${r}-${t}`} style={{ padding: "0 28px", fontSize: 13, fontWeight: 600, letterSpacing: 0.3, color: C.textMuted, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 10 }}>
                  {t}
                </span>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ════ STORY 1: FIND YOUR GAME ════ */}
      <section style={{ padding: isMobile ? "40px 16px" : "100px 40px", background: C.bg, overflow: "hidden" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 40 : 80, alignItems: "center" }}>
          <motion.div variants={fadeLeft} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <span className="section-number">01 — Sessions</span>
            <h2 className="heading" style={{ fontSize: isMobile ? 30 : 60, fontWeight: 900, letterSpacing: -1.5, color: C.text, marginTop: 12, marginBottom: 16, lineHeight: 0.95 }}>
              Find your game.<br /><span className="grad-text">Tonight.</span>
            </h2>
            <p style={{ fontSize: isMobile ? 15 : 17, color: C.textSecondary, lineHeight: 1.72, marginBottom: 24, maxWidth: 480 }}>
              Hundreds of open sessions across every sport. See who's going, how many spots are left, and join with one tap. No group chats. No back and forth. Just sport.
            </p>
            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: "grid", gap: 10, marginBottom: 28 }}>
              {[
                { icon: faMapMarkerAlt, t: "Live map", d: "See active sessions and athletes near you in real time." },
                { icon: faUsers, t: "Join any session", d: "Paid or free. Football, basketball, fitness, tennis, padel, and more." },
                { icon: faLocationDot, t: "Travel anywhere", d: "Spain, France, Thailand — the map works worldwide. Never miss a game." },
              ].map(f => (
                <motion.div key={f.t} variants={staggerItem}>
                  <motion.div
                    style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "14px 16px", borderRadius: 14, border: `1px solid ${C.border}`, background: isDark ? "rgba(255,255,255,0.02)" : "rgba(10,29,68,0.02)" }}
                    whileHover={{ borderColor: C.accentBorder, background: C.accentBgSubtle, x: 4 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: C.accentBgLight, border: `1px solid ${C.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <FontAwesomeIcon icon={f.icon} style={{ fontSize: 14, color: C.accent }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{f.t}</div>
                      <div style={{ fontSize: 13, color: C.textSecondary, marginTop: 2, lineHeight: 1.5 }}>{f.d}</div>
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>
            <motion.button
              onClick={() => navigate("/sessions")}
              style={{ background: C.accentGrad, border: "none", color: "#fff", padding: "14px 28px", borderRadius: 12, cursor: "pointer", fontSize: 15, fontWeight: 700, fontFamily: "'Sora',sans-serif", display: "inline-flex", alignItems: "center", gap: 8 }}
              whileHover={{ y: -3, boxShadow: `0 12px 40px ${C.accentGlow}`, filter: "brightness(1.08)" }}
              whileTap={{ scale: 0.97 }}
            >
              Browse sessions <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 13 }} />
            </motion.button>
          </motion.div>

          <motion.div variants={fadeRight} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} style={{ display: "flex", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "relative", height: 480, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Phone rotate={3} width={230}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Nearby</div>
                <div style={{ height: 120, borderRadius: 10, background: "linear-gradient(135deg,rgba(43,127,255,0.15),rgba(0,212,255,0.08))", border: `1px solid ${C.accentBorder}`, marginBottom: 8, position: "relative", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 24, opacity: 0.5 }}>🗺️</span>
                  {[{ top: "30%", left: "25%", c: "#22D3A5" }, { top: "55%", left: "60%", c: C.accent }, { top: "20%", left: "65%", c: "#FFB800" }].map((p, i) => (
                    <div key={i} style={{ position: "absolute", top: p.top, left: p.left, width: 10, height: 10, borderRadius: "50%", background: p.c, boxShadow: `0 0 0 3px ${p.c}44` }} />
                  ))}
                  <div style={{ position: "absolute", bottom: 6, right: 8, background: "rgba(0,0,0,0.6)", borderRadius: 6, padding: "3px 7px", fontSize: 9, color: "#22D3A5", fontWeight: 700 }}>247 active</div>
                </div>
                {[
                  { sport: "⚽", name: "5-a-side", loc: "0.8km", spots: "3 left", hot: true },
                  { sport: "🏀", name: "Pickup ball", loc: "1.2km", spots: "5 left", hot: false },
                  { sport: "🎾", name: "Tennis doubles", loc: "1.5km", spots: "2 left", hot: false },
                  { sport: "💪", name: "Fitness group", loc: "2.1km", spots: "8 left", hot: false },
                ].map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 10px", borderRadius: 10, marginBottom: 5, background: s.hot ? C.accentBgSubtle : "rgba(255,255,255,0.03)", border: `1px solid ${s.hot ? C.accentBorder : "rgba(255,255,255,0.06)"}` }}>
                    <span style={{ fontSize: 14 }}>{s.sport}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#F0F0FF" }}>{s.name}</div>
                      <div style={{ fontSize: 9, color: "rgba(240,240,255,0.45)" }}>{s.loc} away</div>
                    </div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: s.hot ? C.accent : "#22D3A5" }}>{s.spots}</div>
                  </div>
                ))}
              </Phone>

              <motion.div
                variants={floatY} animate="animate"
                style={{ position: "absolute", top: 10, left: -30, zIndex: 20, background: isDark ? "rgba(18,18,31,0.95)" : "rgba(255,255,255,0.95)", backdropFilter: "blur(16px)", border: `1px solid ${C.border}`, borderRadius: 14, padding: "10px 14px", boxShadow: "0 12px 36px rgba(0,0,0,0.3)" }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: C.text }}>Live near you</div>
                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>12 sessions open tonight</div>
              </motion.div>

              <motion.div
                animate={{ y: [0, -10, 0], rotate: [-3, -3, -3] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                style={{ position: "absolute", bottom: 60, right: -20, zIndex: 20, background: "rgba(34,211,165,0.12)", backdropFilter: "blur(16px)", border: "1px solid rgba(34,211,165,0.25)", borderRadius: 14, padding: "10px 14px" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <span style={{ fontSize: 18 }}>⚽</span>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#22D3A5" }}>Joined!</div>
                    <div style={{ fontSize: 9, color: "rgba(240,240,255,0.55)" }}>Tonight 7pm</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════ STORY 2: TRAIN ════ */}
      <section style={{ padding: isMobile ? "64px 20px" : "100px 40px", background: C.bgAlt, overflow: "hidden" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 40 : 80, alignItems: "center" }}>
          <motion.div variants={fadeLeft} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} style={{ display: "flex", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "relative", height: isMobile ? 380 : 500, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Phone rotate={-3} width={230}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#22D3A5", textTransform: "uppercase", letterSpacing: 0.5 }}>Week 3</div>
                  <div style={{ fontSize: 10, background: "rgba(34,211,165,0.15)", border: "1px solid rgba(34,211,165,0.25)", padding: "2px 8px", borderRadius: 6, color: "#22D3A5", fontWeight: 700 }}>🔥 14 day streak</div>
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                  {[{ c: "#22D3A5", val: 78, label: "Steps" }, { c: C.accent, val: 65, label: "Active" }, { c: "#FFB800", val: 90, label: "Calories" }].map((r, i) => (
                    <div key={i} style={{ flex: 1, textAlign: "center", padding: "10px 6px", borderRadius: 10, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                      <div style={{ fontSize: 16, fontWeight: 900, color: r.c }}>{r.val}%</div>
                      <div style={{ fontSize: 9, color: "rgba(240,240,255,0.45)", marginTop: 2 }}>{r.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(240,240,255,0.5)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Today's workout</div>
                {[{ e: "Push-ups", s: "4×20", done: true }, { e: "Pull-ups", s: "3×10", done: true }, { e: "Dips", s: "3×15", done: false }, { e: "Plank", s: "3×60s", done: false }].map((w, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 8, marginBottom: 4, background: w.done ? "rgba(34,211,165,0.06)" : "rgba(255,255,255,0.03)", border: `1px solid ${w.done ? "rgba(34,211,165,0.2)" : "rgba(255,255,255,0.06)"}` }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", background: w.done ? "#22D3A5" : "rgba(255,255,255,0.1)", border: `1px solid ${w.done ? "#22D3A5" : "rgba(255,255,255,0.15)"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 9, color: "#000" }}>{w.done ? "✓" : ""}</div>
                    <span style={{ flex: 1, fontSize: 11, color: w.done ? "rgba(240,240,255,0.6)" : "#F0F0FF", fontWeight: 500, textDecoration: w.done ? "line-through" : "none" }}>{w.e}</span>
                    <span style={{ fontSize: 10, color: "rgba(240,240,255,0.4)", fontFamily: "'Space Mono',monospace" }}>{w.s}</span>
                  </div>
                ))}
                <div style={{ marginTop: 8, padding: "10px", borderRadius: 10, background: C.accentGrad, color: "#fff", fontSize: 11, fontWeight: 700, textAlign: "center" }}>+240 XP · Complete workout →</div>
              </Phone>

              <motion.div
                variants={floatY} animate="animate"
                style={{ position: "absolute", top: 20, right: -25, zIndex: 20, background: isDark ? "rgba(18,18,31,0.95)" : "rgba(255,255,255,0.95)", backdropFilter: "blur(16px)", border: `1px solid ${C.border}`, borderRadius: 14, padding: "12px 14px" }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 4 }}>👥 Workout Group</div>
                {["Jake", "Emma", "Rian"].map((n, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                    <div style={{ width: 18, height: 18, borderRadius: "50%", background: C.accentBgLight, border: `1px solid ${C.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: C.accent, fontWeight: 700 }}>{n[0]}</div>
                    <span style={{ fontSize: 10, color: C.textMuted }}>{n} · logged today</span>
                  </div>
                ))}
              </motion.div>

              <motion.div
                animate={{ y: [0, -10, 0], rotate: [-3, -3, -3] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                style={{ position: "absolute", bottom: 40, left: -20, zIndex: 20, background: "rgba(255,184,0,0.12)", backdropFilter: "blur(16px)", border: "1px solid rgba(255,184,0,0.25)", borderRadius: 14, padding: "10px 14px" }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: "#FFB800" }}>🍎 Today's nutrition</div>
                <div style={{ fontSize: 10, color: "rgba(240,240,255,0.55)", marginTop: 2 }}>1,840 / 2,200 cal</div>
                <div style={{ marginTop: 5, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.1)" }}>
                  <div style={{ width: "84%", height: 4, borderRadius: 2, background: "#FFB800" }} />
                </div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div variants={fadeRight} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <span className="section-number">02 — Train</span>
            <h2 className="heading" style={{ fontSize: isMobile ? 34 : 60, fontWeight: 900, letterSpacing: -1.5, color: C.text, marginTop: 12, marginBottom: 16, lineHeight: 0.95 }}>
              Every rep.<br /><span className="grad-text">Tracked.</span>
            </h2>
            <p style={{ fontSize: isMobile ? 15 : 17, color: C.textSecondary, lineHeight: 1.72, marginBottom: 24, maxWidth: 480 }}>
              Workouts, calories, steps, GPS routes. Shared with friends or a coach. Train solo or in groups. See each other's streaks, stats, and progress. Accountability built in.
            </p>
            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 28 }}>
              {[
                { icon: faDumbbell, t: "Workout groups", d: "Train together, track together." },
                { icon: faUtensils, t: "Calorie tracker", d: "Log food, macros, streaks." },
                { icon: faRoute, t: "GPS activity", d: "Runs, rides, mile challenges." },
                { icon: faShoePrints, t: "Step tracking", d: "Daily goals with live rankings." },
                { icon: faHeartPulse, t: "Calisthenics tree", d: "Skill progressions from beginner." },
                { icon: faPersonRunning, t: "Run clubs", d: "Social running with groups." },
              ].map(f => (
                <motion.div
                  key={f.t}
                  variants={staggerItem}
                  whileHover={{ y: -5, borderColor: C.accentBorder, boxShadow: `0 12px 36px rgba(43,127,255,0.14)` }}
                  style={{ padding: "14px 14px", borderRadius: 14, background: isDark ? "rgba(255,255,255,0.03)" : "rgba(10,29,68,0.03)", border: `1px solid ${C.border}`, cursor: "default" }}
                  transition={{ duration: 0.28 }}
                >
                  <FontAwesomeIcon icon={f.icon} style={{ fontSize: 13, color: C.accent, marginBottom: 6, display: "block" }} />
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text, marginBottom: 3 }}>{f.t}</div>
                  <div style={{ fontSize: 11.5, color: C.textSecondary, lineHeight: 1.4 }}>{f.d}</div>
                </motion.div>
              ))}
            </motion.div>
            <motion.a
              href="https://linktr.ee/snowsproam" target="_blank" rel="noopener noreferrer"
              style={{ background: C.accentGrad, border: "none", color: "#fff", padding: "14px 28px", borderRadius: 12, cursor: "pointer", fontSize: 15, fontWeight: 700, fontFamily: "'Sora',sans-serif", display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}
              whileHover={{ y: -3, boxShadow: `0 12px 40px ${C.accentGlow}` }}
              whileTap={{ scale: 0.97 }}
            >
              Start training free <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 13 }} />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* ════ STORY 3: COMPETE ════ */}
      <section style={{ padding: isMobile ? "64px 20px" : "100px 40px", background: C.bg, overflow: "hidden" }} className="mesh-bg-blue">
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 40 : 80, alignItems: "center" }}>
          <motion.div variants={fadeLeft} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <span className="section-number">03 — Compete</span>
            <h2 className="heading" style={{ fontSize: isMobile ? 34 : 60, fontWeight: 900, letterSpacing: -1.5, color: C.text, marginTop: 12, marginBottom: 16, lineHeight: 0.95 }}>
              The leaderboard<br /><span className="grad-text">never lies.</span>
            </h2>
            <p style={{ fontSize: isMobile ? 15 : 17, color: C.textSecondary, lineHeight: 1.72, marginBottom: 24, maxWidth: 480 }}>
              ELO matchmaking. Knockout tournaments. Local to global rankings. Put your trophies on the line in competitive mode or play casual. Every sport. Every level.
            </p>
            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: "grid", gap: 10, marginBottom: 28 }}>
              {[
                { icon: faTrophy, t: "ELO matchmaking", d: "Skill-rated pairing in competitive or casual mode. Trophies at stake." },
                { icon: faUsers, t: "Tournaments", d: "Full bracket management. Knockout, league, or admin-seeded formats." },
                { icon: faChartLine, t: "Local to global", d: "Rank regionally, nationally, globally. Find rivals near you." },
              ].map(f => (
                <motion.div key={f.t} variants={staggerItem}>
                  <motion.div
                    style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "14px 16px", borderRadius: 14, border: `1px solid ${C.border}`, background: isDark ? "rgba(255,255,255,0.02)" : "rgba(10,29,68,0.02)" }}
                    whileHover={{ borderColor: C.accentBorder, x: 4 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: C.accentBgLight, border: `1px solid ${C.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <FontAwesomeIcon icon={f.icon} style={{ fontSize: 14, color: C.accent }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{f.t}</div>
                      <div style={{ fontSize: 13, color: C.textSecondary, marginTop: 2, lineHeight: 1.5 }}>{f.d}</div>
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>
            <motion.button
              onClick={() => navigate("/leaderboard")}
              style={{ background: C.accentGrad, border: "none", color: "#fff", padding: "14px 28px", borderRadius: 12, cursor: "pointer", fontSize: 15, fontWeight: 700, fontFamily: "'Sora',sans-serif", display: "inline-flex", alignItems: "center", gap: 8 }}
              whileHover={{ y: -3, boxShadow: `0 12px 40px ${C.accentGlow}` }}
              whileTap={{ scale: 0.97 }}
            >
              See leaderboard <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 13 }} />
            </motion.button>
          </motion.div>

          <motion.div variants={fadeRight} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} style={{ display: "flex", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "relative", height: isMobile ? 380 : 500, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Phone rotate={2} width={240}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 16 }}>🏆</span>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#F0F0FF" }}>National Rankings</div>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 6, marginBottom: 10, height: 60 }}>
                  {[{ n: "Sophie", pts: "11,560", h: 44, medal: "🥈", pos: 2 }, { n: "Ciarán", pts: "12,840", h: 60, medal: "🥇", pos: 1 }, { n: "Rian", pts: "10,290", h: 36, medal: "🥉", pos: 3 }].map((e, i) => (
                    <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                      <div style={{ fontSize: 11 }}>{e.medal}</div>
                      <div style={{ fontSize: 9, color: "rgba(240,240,255,0.6)", fontWeight: 600 }}>{e.n}</div>
                      <div style={{ width: 44, height: e.h, borderRadius: "6px 6px 0 0", background: i === 1 ? "linear-gradient(180deg,#FFB800,rgba(255,184,0,0.4))" : i === 0 ? "linear-gradient(180deg,rgba(200,200,200,0.6),rgba(200,200,200,0.2))" : "linear-gradient(180deg,rgba(205,127,50,0.7),rgba(205,127,50,0.2))" }} />
                    </div>
                  ))}
                </div>
                {[
                  { pos: 1, name: "Ciarán M.", pts: "12,840", badge: "🥇", up: true },
                  { pos: 2, name: "Sophie R.", pts: "11,560", badge: "🥈", up: true },
                  { pos: 3, name: "Rian D.", pts: "10,290", badge: "🥉", up: false },
                  { pos: 4, name: "Emma W.", pts: "9,870", badge: null, up: true },
                  { pos: 5, name: "You?", pts: "Join →", badge: null, up: null, me: true },
                ].map((e, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 10px", borderRadius: 9, marginBottom: 4, background: e.me ? `linear-gradient(135deg,${C.accentBgSubtle},transparent)` : "rgba(255,255,255,0.02)", border: `1px solid ${e.me ? C.accentBorder : "rgba(255,255,255,0.06)"}` }}>
                    <span style={{ width: 16, textAlign: "center", fontSize: 11, fontWeight: 700, color: i < 3 ? "#FFB800" : "rgba(240,240,255,0.3)", fontFamily: "'Space Mono',monospace" }}>{e.pos}</span>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: e.me ? C.accentBgLight : "rgba(255,255,255,0.06)", border: `1px solid ${e.me ? C.accentBorder : "rgba(255,255,255,0.08)"}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>{e.badge || "👤"}</div>
                    <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: e.me ? C.accent : "#F0F0FF" }}>{e.name}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: e.me ? C.accent : "rgba(240,240,255,0.5)", fontFamily: "'Space Mono',monospace" }}>{e.pts}</span>
                    {e.up !== null && <FontAwesomeIcon icon={faArrowTrendUp} style={{ fontSize: 9, color: e.up ? "#22D3A5" : "#FF4D6A", transform: e.up ? "none" : "scaleY(-1)" }} />}
                  </div>
                ))}
              </Phone>

              <motion.div
                variants={floatY} animate="animate"
                style={{ position: "absolute", top: 20, left: -25, zIndex: 20, background: isDark ? "rgba(18,18,31,0.95)" : "rgba(255,255,255,0.95)", backdropFilter: "blur(16px)", border: `1px solid ${C.border}`, borderRadius: 14, padding: "10px 14px" }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: "#FFB800" }}>🏆 Tournament tonight</div>
                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>5-a-side · 8 teams</div>
              </motion.div>

              <motion.div
                animate={{ y: [0, -10, 0], rotate: [-3, -3, -3] }}
                transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
                style={{ position: "absolute", bottom: 50, right: -15, zIndex: 20, background: "rgba(43,127,255,0.12)", backdropFilter: "blur(16px)", border: `1px solid ${C.accentBorder}`, borderRadius: 14, padding: "10px 14px" }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: C.accent }}>📈 Rank up!</div>
                <div style={{ fontSize: 10, color: "rgba(240,240,255,0.55)", marginTop: 2 }}>#4 to #3 Ireland</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════ STORY 4: CONNECT ════ */}
      <section style={{ padding: isMobile ? "64px 20px" : "100px 40px", background: C.bgAlt, overflow: "hidden" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 40 : 80, alignItems: "center" }}>
          <motion.div variants={fadeLeft} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} style={{ display: "flex", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "relative", height: isMobile ? 380 : 500, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Phone rotate={-2} width={230}>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>Football · Dublin</div>
                <div style={{ borderRadius: 10, overflow: "hidden", marginBottom: 8, border: `1px solid ${C.border}`, background: "rgba(255,255,255,0.03)" }}>
                  <div style={{ height: 70, background: "linear-gradient(135deg,rgba(43,127,255,0.2),rgba(0,212,255,0.1))", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <FontAwesomeIcon icon={faPlay} style={{ fontSize: 10, color: "#fff", marginLeft: 2 }} />
                    </div>
                  </div>
                  <div style={{ padding: "8px 10px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#F0F0FF" }}>Goal of the week 🔥</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", background: C.accentBgLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, color: C.accent, fontWeight: 700 }}>C</div>
                      <span style={{ fontSize: 10, color: "rgba(240,240,255,0.5)" }}>Ciarán M.</span>
                      <span style={{ fontSize: 10, color: "rgba(240,240,255,0.35)" }}>· 127 👏</span>
                    </div>
                  </div>
                </div>
                {[
                  { avatar: "S", name: "Sophie R.", msg: "Anyone for 7-a-side Saturday?", time: "3m", reactions: "8" },
                  { avatar: "J", name: "Jake T.", msg: "Best boots for astroturf?", time: "12m", reactions: "4" },
                  { avatar: "A", name: "Aoife K.", msg: "5-a-side wins tonight 🏆", time: "1h", reactions: "23" },
                ].map((p, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, padding: "9px 0", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: C.accentBgLight, border: `1px solid ${C.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: C.accent, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>{p.avatar}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: C.accent }}>{p.name}</span>
                        <span style={{ fontSize: 9, color: "rgba(240,240,255,0.3)" }}>{p.time}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "rgba(240,240,255,0.75)", marginTop: 2 }}>{p.msg}</div>
                      <div style={{ fontSize: 9, color: "rgba(240,240,255,0.35)", marginTop: 3 }}>👏 {p.reactions}</div>
                    </div>
                  </div>
                ))}
              </Phone>

              <motion.div
                variants={floatY} animate="animate"
                style={{ position: "absolute", top: 10, right: -20, zIndex: 20, background: isDark ? "rgba(18,18,31,0.95)" : "rgba(255,255,255,0.95)", backdropFilter: "blur(16px)", border: `1px solid ${C.border}`, borderRadius: 14, padding: "10px 14px" }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: C.text }}>⚽ Football Dublin</div>
                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>1.2k members · 34 new</div>
              </motion.div>

              <motion.div
                animate={{ y: [0, -10, 0], rotate: [-3, -3, -3] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                style={{ position: "absolute", bottom: 60, left: -20, zIndex: 20, background: "rgba(43,127,255,0.12)", backdropFilter: "blur(16px)", border: `1px solid ${C.accentBorder}`, borderRadius: 14, padding: "10px 14px" }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: C.accent }}>💬 New message</div>
                <div style={{ fontSize: 10, color: "rgba(240,240,255,0.55)", marginTop: 2 }}>Ciarán: Up for a game?</div>
              </motion.div>
            </div>
          </motion.div>

          <motion.div variants={fadeRight} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
            <span className="section-number">04 — Connect</span>
            <h2 className="heading" style={{ fontSize: isMobile ? 34 : 60, fontWeight: 900, letterSpacing: -1.5, color: C.text, marginTop: 12, marginBottom: 16, lineHeight: 0.95 }}>
              Your sport.<br /><span className="grad-text">Your people.</span>
            </h2>
            <p style={{ fontSize: isMobile ? 15 : 17, color: C.textSecondary, lineHeight: 1.72, marginBottom: 24, maxWidth: 480 }}>
              Every sport has its own world. Forums, communities, highlights feed, direct chat. Find athletes through the leaderboard. No algorithm. No noise. Just sport.
            </p>
            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 28 }}>
              {[
                { icon: faComments, t: "Sport forums", d: "Discussion, tips, results per sport." },
                { icon: faUsers, t: "Communities", d: "Find and join your people." },
                { icon: faPlay, t: "Highlights feed", d: "Post clips. Watch real sport." },
                { icon: faGlobe, t: "World chat", d: "Country channels per sport." },
              ].map(f => (
                <motion.div
                  key={f.t}
                  variants={staggerItem}
                  whileHover={{ y: -5, borderColor: C.accentBorder, boxShadow: `0 12px 36px rgba(43,127,255,0.14)` }}
                  style={{ padding: "14px 14px", borderRadius: 14, background: isDark ? "rgba(255,255,255,0.03)" : "rgba(10,29,68,0.03)", border: `1px solid ${C.border}` }}
                  transition={{ duration: 0.28 }}
                >
                  <FontAwesomeIcon icon={f.icon} style={{ fontSize: 13, color: C.accent, marginBottom: 6, display: "block" }} />
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text, marginBottom: 3 }}>{f.t}</div>
                  <div style={{ fontSize: 11.5, color: C.textSecondary, lineHeight: 1.4 }}>{f.d}</div>
                </motion.div>
              ))}
            </motion.div>
            <motion.button
              onClick={() => navigate("/sessions")}
              style={{ background: C.accentGrad, border: "none", color: "#fff", padding: "14px 28px", borderRadius: 12, cursor: "pointer", fontSize: 15, fontWeight: 700, fontFamily: "'Sora',sans-serif", display: "inline-flex", alignItems: "center", gap: 8 }}
              whileHover={{ y: -3, boxShadow: `0 12px 40px ${C.accentGlow}` }}
              whileTap={{ scale: 0.97 }}
            >
              Find your community <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 13 }} />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* ════ PROFILE SECTION ════ */}
      <section style={{ padding: isMobile ? "64px 20px" : "100px 40px", background: C.bg }} className="mesh-bg-blue">
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ textAlign: "center", marginBottom: 44 }}>
            <span className="section-number">Your identity</span>
            <h2 className="heading" style={{ fontSize: isMobile ? 30 : 56, fontWeight: 900, letterSpacing: -1.5, color: C.text, marginTop: 12, marginBottom: 14, lineHeight: 1 }}>
              One profile.<br /><span className="grad-text">Every sport.</span>
            </h2>
            <p style={{ fontSize: isMobile ? 15 : 17, color: C.textSecondary, maxWidth: 560, margin: "0 auto", lineHeight: 1.7 }}>
              Build a sporting identity that follows you across every ecosystem. Your ELO, trophies, highlights, streaks, and achievements all in one place.
            </p>
          </motion.div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 12 }}>
            {[
              { num: "01", icon: faTrophy, t: "Sport profiles", d: "Separate ELO, trophies, and stats per sport. Your boxing record stays separate from your running times." },
              { num: "02", icon: faStar, t: "XP and achievements", d: "Earn XP, unlock badges, complete milestones. Every session, workout, and tournament adds to your legacy." },
              { num: "03", icon: faPlay, t: "Highlights reel", d: "Post match clips, workout moments, milestones. Build a profile that shows your game, not just stats." },
              { num: "04", icon: faUsers, t: "ProAm Connect", d: "Discover athletes through the leaderboard. View their profile, highlights, and send a connection." },
            ].map(f => (
              <motion.div
                key={f.num}
                variants={staggerItem}
                className="glass-card"
                whileHover={{ y: -6, boxShadow: `0 20px 60px rgba(43,127,255,0.16)` }}
                style={{ padding: isMobile ? 16 : 24 }}
                transition={{ duration: 0.3 }}
              >
                <div className="mono" style={{ fontSize: isMobile ? 28 : 38, fontWeight: 700, color: `${C.accent}18`, lineHeight: 1, marginBottom: 12, userSelect: "none" }}>{f.num}</div>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: C.accentBgLight, border: `1px solid ${C.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                  <FontAwesomeIcon icon={f.icon} style={{ fontSize: 14, color: C.accent }} />
                </div>
                <div className="heading" style={{ fontSize: isMobile ? 13 : 15, fontWeight: 800, color: C.text, marginBottom: 6 }}>{f.t}</div>
                <p style={{ fontSize: isMobile ? 12 : 13, color: C.textSecondary, lineHeight: 1.6 }}>{f.d}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ════ STATS BAND ════ */}
      <section id="stats-band" style={{ padding: isMobile ? "48px 20px" : "72px 24px", background: C.bgAlt }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ textAlign: "center", marginBottom: 36 }}>
            <span className="section-number">The movement</span>
            <h2 className="heading" style={{ fontSize: isMobile ? 26 : 44, fontWeight: 900, letterSpacing: -1, color: C.text, marginTop: 10 }}>
              Athletes are moving <span className="grad-text">right now</span>
            </h2>
          </motion.div>
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 12 }}>
            {[
              { num: "1K+", label: "Athletes worldwide", sub: "and growing daily" },
              { num: "Multi", label: "Sports ecosystem", sub: "football, fitness and more" },
              { num: "Live", label: "Leaderboards", sub: "real-time rankings" },
              { num: "Free", label: "For individuals", sub: "forever for athletes" },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                variants={staggerItem}
                className="glass-card"
                whileHover={{ y: -5, boxShadow: `0 16px 48px rgba(43,127,255,0.14)` }}
                style={{ padding: isMobile ? "18px 14px" : "28px 24px", textAlign: "center" }}
                transition={{ duration: 0.28 }}
              >
                <div className="kinetic-num" style={{ fontSize: isMobile ? 28 : 44 }}>{s.num}</div>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: C.text, marginTop: 6 }}>{s.label}</div>
                <div style={{ fontSize: isMobile ? 11 : 12, color: C.textMuted, marginTop: 3 }}>{s.sub}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ════ SPORT GRID ════ */}
      <section style={{ padding: isMobile ? "52px 20px" : "80px 24px", background: C.bg }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ marginBottom: 28 }}>
            <span className="section-number">Live now</span>
            <h2 className="heading" style={{ fontSize: isMobile ? 26 : 44, fontWeight: 900, letterSpacing: -1, color: C.text, marginTop: 10, marginBottom: 10 }}>
              Find your sport<span className="grad-text">, more on the way</span>
            </h2>
            <p style={{ fontSize: isMobile ? 14 : 16, color: C.textSecondary, maxWidth: 520, lineHeight: 1.65 }}>
              Every sport has its own world. Community, leaderboard, sessions, events, athlete tracker and forum.
            </p>
          </motion.div>
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {sportsLoading
              ? [...Array(10)].map((_, i) => <div key={i} style={{ width: 130, height: 44, borderRadius: 11, background: C.bgCard, border: `1px solid ${C.border}` }} />)
              : sports.map(sport => (
                <motion.div
                  key={sport.id}
                  variants={staggerItem}
                  whileHover={{ y: -3, borderColor: `${C.accent}55`, boxShadow: `0 8px 24px ${C.accentGlow}`, background: C.accentBgLight }}
                  style={{ padding: "9px 14px", borderRadius: 11, background: C.bgCard, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8, cursor: "default" }}
                  transition={{ duration: 0.22 }}
                >
                  <img src={sport.icon_url} alt={sport.id} style={{ width: 18, height: 18, objectFit: "contain" }}
                    onError={e => { e.target.onerror = null; e.target.src = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><text y="20" font-size="20">🏆</text></svg>')}`; }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.text, textTransform: "capitalize", whiteSpace: "nowrap" }}>{sport.id.replace(/_/g, " ")}</span>
                </motion.div>
              ))
            }
          </motion.div>
        </div>
      </section>

      {/* ════ SCHOOL LEAGUE ════ */}
      <section id="schools" style={{ padding: isMobile ? "56px 20px" : "96px 24px", background: C.bgAlt, scrollMarginTop: 72 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ borderRadius: 20, overflow: "hidden", background: C.bgCard, border: `1px solid ${C.border}`, boxShadow: C.shadowLg }}>
            <div style={{ padding: isMobile ? "24px 20px" : "40px 44px", background: isDark ? `linear-gradient(135deg,rgba(43,127,255,0.08),transparent)` : `linear-gradient(135deg,rgba(26,111,235,0.05),transparent)`, borderBottom: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", gap: 16, flexDirection: isMobile ? "column" : "row" }}>
                <img src="https://media.snowsproam.com/Schools/StepsChallenge-Ireland/school.png" alt="" loading="lazy" style={{ width: isMobile ? 72 : 80, height: "auto", objectFit: "contain", flexShrink: 0 }} />
                <div>
                  <span className="section-number">Irish School League</span>
                  <h2 className="heading" style={{ fontSize: isMobile ? 22 : 36, fontWeight: 900, letterSpacing: -0.8, color: C.text, marginTop: 8, marginBottom: 8 }}>
                    Irish Secondary School <span className="grad-text">Step Challenge</span>
                  </h2>
                  <p style={{ fontSize: isMobile ? 14 : 15, color: C.textSecondary, maxWidth: 560, lineHeight: 1.65 }}>
                    Schools across Ireland are moving together. Follow live steps leaderboards, celebrate students’ effort, and bring your school into the challenge.
                  </p>
                </div>
              </div>
            </div>
            <div style={{ padding: isMobile ? 20 : 44 }}>
              <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3,1fr)", gap: 10, marginBottom: 24 }}>
                {[
                  { icon: faChartLine, t: "Live Leaderboards", d: "Track classes and schools nationwide in real time." },
                  { icon: faMedal, t: "Awards and Recognition", d: "Celebrate top steppers and most improved students." },
                  { icon: faRocket, t: "Simple Setup", d: "Create your school page, invite students, done." },
                  { icon: faUsers, t: "Inclusive by Design", d: "Every step counts — walking, training, daily movement." },
                  { icon: faLock, t: "Safeguarded", d: "Staff-managed profiles. Privacy is fundamental." },
                  { icon: faHeartPulse, t: "Lasting Impact", d: "Build habits that support students long-term." },
                ].map(x => (
                  <motion.div
                    key={x.t}
                    variants={staggerItem}
                    whileHover={{ y: -4, borderColor: C.accentBorder }}
                    style={{ padding: isMobile ? "14px 12px" : "18px 16px", borderRadius: 12, background: C.bgAlt, border: `1px solid ${C.border}` }}
                    transition={{ duration: 0.22 }}
                  >
                    <FontAwesomeIcon icon={x.icon} style={{ fontSize: 13, color: C.accent, marginBottom: 8, display: "block" }} />
                    <div style={{ fontSize: isMobile ? 12 : 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>{x.t}</div>
                    <div style={{ fontSize: isMobile ? 11 : 13, color: C.textSecondary, lineHeight: 1.5 }}>{x.d}</div>
                  </motion.div>
                ))}
              </motion.div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 24 }}>
                <motion.button
                  onClick={() => navigate("/instituteleaderboard")}
                  style={{ background: C.accentGrad, border: "none", color: "#fff", padding: "13px 22px", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: "'Sora',sans-serif", display: "inline-flex", alignItems: "center", gap: 8 }}
                  whileHover={{ y: -2, boxShadow: `0 10px 32px ${C.accentGlow}` }}
                  whileTap={{ scale: 0.97 }}
                >
                  View Rankings <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12 }} />
                </motion.button>
                <motion.button
                  onClick={() => { setIsModalOpen(true); setIsSignUp(false); setError(""); }}
                  style={{ background: C.bgCard, border: `1px solid ${C.borderMedium}`, color: C.text, padding: "13px 20px", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 600, boxShadow: C.shadowSm }}
                  whileHover={{ y: -2, borderColor: C.accentBorder }}
                  whileTap={{ scale: 0.97 }}
                >
                  Sign Your School Up
                </motion.button>
              </div>
              <div style={{ marginBottom: 24 }}>
                <p style={{ fontSize: isMobile ? 12 : 13, color: C.textSecondary, lineHeight: 1.6 }}>World Wide Steps is a Snows ProAm project.</p>
                <a href="https://worldwidesteps.com/schools" style={{ display: "inline-flex", alignItems: "center", gap: 8, minHeight: 44, maxWidth: "100%", color: C.accent, fontSize: isMobile ? 13 : 14, fontWeight: 700, lineHeight: 1.5, textDecoration: "underline", textUnderlineOffset: 3 }}><span>See the live Irish school step challenge</span><FontAwesomeIcon icon={faArrowRight} aria-hidden="true" style={{ flexShrink: 0 }} /></a>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Resources</div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3,1fr)", gap: 8 }}>
                {resources.map(r => (
                  <motion.a
                    key={r.title}
                    href={r.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ padding: "12px 12px", borderRadius: 10, background: C.bgAlt, border: `1px solid ${C.border}`, display: "block" }}
                    whileHover={{ y: -3, borderColor: C.accentBorder, background: C.accentBgSubtle }}
                    transition={{ duration: 0.2 }}
                  >
                    <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: C.text, marginBottom: 2 }}>{r.title}</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>{r.meta}</div>
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════ SPORT IRELAND SWIMMING ════ */}
      <section style={{ maxWidth: 1200, margin: "0 auto", padding: isMobile ? "52px 20px" : "80px 24px" }}>
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          style={{ borderRadius: 20, background: C.accentGrad, padding: isMobile ? "32px 24px" : "56px 48px", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", gap: 24, boxShadow: `0 16px 60px ${C.accentGlow}`, position: "relative", overflow: "hidden" }}
        >
          <div style={{ position: "absolute", top: -60, right: -60, width: 280, height: 280, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
          <div style={{ position: "relative", zIndex: 1, flex: 1 }}>
            <div className="mono" style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.75)", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10 }}>Sport Ireland Campus</div>
            <h2 style={{ fontSize: isMobile ? 22 : 38, fontWeight: 900, color: "#fff", letterSpacing: -0.8, marginBottom: 12 }}>Private Swim Lessons at Sport Ireland</h2>
            <p style={{ fontSize: isMobile ? 14 : 15, color: "rgba(255,255,255,0.82)", lineHeight: 1.65, maxWidth: 500 }}>
              Book 1-on-1 swimming sessions at the Sport Ireland Campus pool. Professional coaching, flexible scheduling, personalised for all skill levels.
            </p>
          </div>
          <motion.button
            onClick={() => navigate("/sport-ireland/swimming")}
            style={{ position: "relative", zIndex: 1, background: "#fff", color: "#0A1D44", border: "none", padding: "14px 28px", borderRadius: 12, cursor: "pointer", fontSize: 15, fontWeight: 800, fontFamily: "'Sora',sans-serif", display: "inline-flex", alignItems: "center", gap: 8, boxShadow: "0 8px 28px rgba(0,0,0,0.2)", whiteSpace: "nowrap", width: isMobile ? "100%" : "auto", justifyContent: isMobile ? "center" : "flex-start" }}
            whileHover={{ y: -3, boxShadow: "0 16px 48px rgba(0,0,0,0.28)" }}
            whileTap={{ scale: 0.97 }}
          >
            Book Now →
          </motion.button>
        </motion.div>
      </section>

      {/* ════ COMPANIES ════ */}
      <section id="companies" style={{ padding: isMobile ? "56px 20px" : "96px 24px", background: C.bgAlt }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ marginBottom: 36 }}>
            <span className="section-number">For Companies</span>
            <h2 className="heading" style={{ fontSize: isMobile ? 30 : 52, fontWeight: 900, letterSpacing: -1.2, color: C.text, marginTop: 10, marginBottom: 10 }}>
              Sport-powered <span className="grad-text">employee wellbeing</span>
            </h2>
            <p style={{ fontSize: isMobile ? 14 : 16, color: C.textSecondary, maxWidth: 520 }}>Build a culture where people actually move together.</p>
          </motion.div>
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 0, borderRadius: 16, overflow: "hidden", border: `1px solid ${C.border}`, boxShadow: C.shadowMd, marginBottom: 16, background: C.bgCard }}
          >
            <div style={{ padding: isMobile ? "20px 18px" : "24px 22px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: C.textMuted, marginBottom: 12 }}>Without ProAm</div>
              {["Wellbeing apps nobody opens after week 2", "Step challenges with no social layer", "Fitness perks with poor adoption", "No team connection outside the office"].map(p => (
                <div key={p} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, fontSize: isMobile ? 13 : 13.5, color: C.textSecondary }}>
                  <FontAwesomeIcon icon={faTimes} style={{ fontSize: 11, color: C.danger, opacity: 0.7, flexShrink: 0 }} /> {p}
                </div>
              ))}
            </div>
            <div style={{ padding: isMobile ? "20px 18px" : "24px 22px", background: C.accentBgSubtle, borderLeft: isMobile ? "none" : `1px solid ${C.border}`, borderTop: isMobile ? `1px solid ${C.border}` : "none" }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: C.accent, marginBottom: 12 }}>With Snows ProAm</div>
              {["Social sport loops employees return to", "Friendly competitions that drive daily activity", "Community spaces by sport and location", "Events with real attendance tracking"].map(p => (
                <div key={p} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, fontSize: isMobile ? 13 : 13.5, color: C.textSecondary }}>
                  <FontAwesomeIcon icon={faCheck} style={{ fontSize: 11, color: C.success, flexShrink: 0 }} /> {p}
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 12, marginBottom: 20 }}>
            {[
              { icon: faHeartPulse, t: "Wellbeing that sticks", d: "Step challenges, workout groups, sessions — multiple entry points.", tags: ["Challenges", "Groups"] },
              { icon: faTrophy, t: "Internal leagues", d: "Team leagues, 5-a-side tournaments, office step battles.", tags: ["Brackets", "Leaderboards"] },
              { icon: faNetworkWired, t: "Sport communities", d: "Runners, cyclists, gym-goers — organised by sport, not department.", tags: ["Sport groups", "Locations"] },
            ].map(c => (
              <motion.div
                key={c.t}
                variants={staggerItem}
                className="glass-card"
                whileHover={{ y: -5, boxShadow: `0 16px 48px rgba(43,127,255,0.14)` }}
                style={{ padding: isMobile ? 18 : 22 }}
                transition={{ duration: 0.28 }}
              >
                <FontAwesomeIcon icon={c.icon} style={{ fontSize: 16, color: C.accent, marginBottom: 10, display: "block" }} />
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>{c.t}</div>
                <div style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.55, marginBottom: 10 }}>{c.d}</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{c.tags.map(t => <span key={t} style={tag()}>{t}</span>)}</div>
              </motion.div>
            ))}
          </motion.div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <motion.button
              onClick={() => navigate("/support")}
              style={{ background: C.accentGrad, border: "none", color: "#fff", padding: "13px 24px", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: "'Sora',sans-serif", display: "inline-flex", alignItems: "center", gap: 8 }}
              whileHover={{ y: -2, boxShadow: `0 10px 32px ${C.accentGlow}` }}
              whileTap={{ scale: 0.97 }}
            >
              Book a demo <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 12 }} />
            </motion.button>
            <motion.button
              onClick={() => window.open("https://linktr.ee/snowsproam", "_blank")}
              style={{ background: C.bgCard, border: `1px solid ${C.borderMedium}`, color: C.text, padding: "13px 20px", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 600, boxShadow: C.shadowSm }}
              whileHover={{ y: -2, borderColor: C.accentBorder }}
              whileTap={{ scale: 0.97 }}
            >
              Download app
            </motion.button>
          </div>
        </div>
      </section>

      {/* ════ ORGANISATIONS ════ */}
      <section id="organisations" style={{ padding: isMobile ? "56px 20px" : "96px 24px", background: C.bg }} className="mesh-bg-blue">
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ marginBottom: 36 }}>
            <span className="section-number">For Organisations</span>
            <h2 className="heading" style={{ fontSize: isMobile ? 30 : 52, fontWeight: 900, letterSpacing: -1.2, color: C.text, marginTop: 10, marginBottom: 10 }}>
              Programmes people <span className="grad-text">actually use</span>
            </h2>
            <p style={{ fontSize: isMobile ? 14 : 16, color: C.textSecondary, maxWidth: 520 }}>
              Wellbeing and participation built on real engagement, not downloads that gather dust.
            </p>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 320px", gap: 16, alignItems: "start" }}>
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                {[
                  { icon: faFlag, title: "Step and Activity Challenges", desc: "Run school, cohort, or regional step challenges with live leaderboards.", tags: ["Live leaderboard", "Awards"] },
                  { icon: faLayerGroup, title: "Structured Groups", desc: "Organise participants into cohorts, teams, or age groups with separate tracking.", tags: ["Cohorts", "Group targets"] },
                ].map(c => (
                  <motion.div key={c.title} className="glass-card" whileHover={{ y: -4 }} style={{ padding: isMobile ? 18 : 22 }} transition={{ duration: 0.22 }}>
                    <FontAwesomeIcon icon={c.icon} style={{ fontSize: 15, color: C.accent, marginBottom: 10, display: "block" }} />
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>{c.title}</div>
                    <div style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.55, marginBottom: 10 }}>{c.desc}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{c.tags.map(t => <span key={t} style={tag()}>{t}</span>)}</div>
                  </motion.div>
                ))}
              </div>
              <div className="glass-card" style={{ padding: isMobile ? 18 : 22, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 16 }}>
                {[{ icon: faLock, t: "Privacy-first", d: "Staff-managed profiles, block/mute, public and private spaces." }, { icon: faRepeat, t: "Engagement loops", d: "Challenge to sessions to community. People keep showing up." }, { icon: faRocket, t: "Fast rollout", d: "Invite with a code or link. Running in days, not months." }].map(x => (
                  <div key={x.t}>
                    <FontAwesomeIcon icon={x.icon} style={{ fontSize: 14, color: C.accent, marginBottom: 8, display: "block" }} />
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 }}>{x.t}</div>
                    <div style={{ fontSize: 12.5, color: C.textSecondary, lineHeight: 1.5 }}>{x.d}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass-card" style={{ padding: isMobile ? 18 : 22 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, marginBottom: 14 }}>Built for:</div>
              {[
                { icon: faFlag, label: "National sports bodies", desc: "Nationwide participation programmes." },
                { icon: faBuilding, label: "Local authorities", desc: "Community sport and wellbeing initiatives." },
                { icon: faMedal, label: "Sports partnerships", desc: "School leagues, youth programmes." },
                { icon: faPeopleGroup, label: "NGOs and charities", desc: "Social sport and inclusion projects." },
              ].map(u => (
                <motion.div key={u.label} whileHover={{ x: 4, background: C.accentBgSubtle }} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 12px", borderRadius: 10, background: isDark ? "rgba(255,255,255,0.02)" : "rgba(10,29,68,0.02)", border: `1px solid ${C.border}`, marginBottom: 8 }} transition={{ duration: 0.18 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: C.accentBgSubtle, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <FontAwesomeIcon icon={u.icon} style={{ fontSize: 13, color: C.accent }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{u.label}</div>
                    <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{u.desc}</div>
                  </div>
                </motion.div>
              ))}
              <motion.button
                onClick={() => navigate("/support")}
                style={{ width: "100%", marginTop: 10, background: C.accentGrad, border: "none", color: "#fff", padding: "12px", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: "'Sora',sans-serif" }}
                whileHover={{ y: -2, boxShadow: `0 10px 32px ${C.accentGlow}` }}
                whileTap={{ scale: 0.97 }}
              >
                Speak with us
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════ RELAY FOR LIFE ════ */}
      <section style={{ maxWidth: 1200, margin: "0 auto", padding: isMobile ? "0 20px 52px" : "0 24px 80px" }}>
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          style={{ borderRadius: 20, padding: isMobile ? "32px 24px" : "48px 44px", background: "linear-gradient(135deg,#7C3AED 0%,#2D0A5F 100%)", boxShadow: "0 16px 60px rgba(124,58,237,0.30)", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", gap: 24, position: "relative", overflow: "hidden" }}
        >
          <div style={{ position: "absolute", top: -50, right: -50, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
          <div style={{ position: "relative", zIndex: 1 }}>
            <div className="mono" style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.70)", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 8 }}>🎗️ Relay for Life</div>
            <h3 className="heading" style={{ fontSize: isMobile ? 22 : 34, fontWeight: 900, color: "#fff", marginBottom: 10 }}>View Relay for Life</h3>
            <p style={{ fontSize: isMobile ? 14 : 15, color: "rgba(255,255,255,0.80)", lineHeight: 1.65, maxWidth: 460 }}>
              Explore teams, track progress, and follow the Relay for Life challenge live. Live leaderboard, teams, event progress.
            </p>
          </div>
          <motion.button
            onClick={() => window.open("https://snowsproam.com/relayforlife", "_blank")}
            style={{ position: "relative", zIndex: 1, padding: "14px 28px", borderRadius: 12, border: "none", background: "#F59E0B", color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer", fontFamily: "'Sora',sans-serif", boxShadow: "0 8px 28px rgba(245,158,11,0.35)", whiteSpace: "nowrap", width: isMobile ? "100%" : "auto", display: "flex", alignItems: "center", justifyContent: isMobile ? "center" : "flex-start" }}
            whileHover={{ y: -3, boxShadow: "0 16px 40px rgba(245,158,11,0.5)" }}
            whileTap={{ scale: 0.97 }}
          >
            View Event →
          </motion.button>
        </motion.div>
      </section>

      {/* ════ FAQ ════ */}
      <section style={{ maxWidth: 760, margin: "0 auto", padding: isMobile ? "52px 20px" : "96px 24px" }}>
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ textAlign: "center", marginBottom: 36 }}>
          <span className="section-number">Questions</span>
          <h2 className="heading" style={{ fontSize: isMobile ? 28 : 48, fontWeight: 900, letterSpacing: -1, color: C.text, marginTop: 10 }}>Got questions?</h2>
        </motion.div>
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: "grid", gap: 8 }}>
          {faqs.map((f, i) => (
            <motion.div
              key={f.q}
              variants={staggerItem}
              style={{ borderRadius: 14, border: `1px solid ${openFaq === i ? C.accentBorder : C.border}`, background: openFaq === i ? C.bgCard : "transparent", boxShadow: openFaq === i ? C.shadowMd : "none", transition: "all 0.25s ease" }}
            >
              <motion.button
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                style={{ width: "100%", padding: "16px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, background: "transparent", border: "none", cursor: "pointer", textAlign: "left", color: C.text }}
                whileHover={{ background: C.accentBgSubtle }}
                transition={{ duration: 0.15 }}
              >
                <span style={{ fontSize: isMobile ? 14 : 14.5, fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>{f.q}</span>
                <motion.span animate={{ rotate: openFaq === i ? 90 : 0 }} transition={{ duration: 0.25 }}>
                  <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: 12, color: C.textMuted, flexShrink: 0 }} />
                </motion.span>
              </motion.button>
              <AnimatePresence initial={false}>
                {openFaq === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    style={{ overflow: "hidden" }}
                  >
                    <div style={{ padding: "0 18px 16px", fontSize: 14, color: C.textSecondary, lineHeight: 1.7 }}>{f.a}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ════ SPLINE ════ */}
      <section style={{ background: "#080810", padding: isMobile ? "28px 20px 52px" : "32px 24px 96px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <div className="mono" style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.30)", textTransform: "uppercase", letterSpacing: 2, marginBottom: 12 }}>
              Interactive — {isMobile ? "2 fingers to orbit" : "click and drag to explore"}
            </div>
            <div style={{ width: "100%", height: isMobile ? "50vh" : "68vh", minHeight: 280, borderRadius: 18, overflow: "hidden", border: "1px solid rgba(255,255,255,0.07)", background: "linear-gradient(135deg,#0A1D44 0%,#0C69C8 50%,#080810 100%)", boxShadow: "0 16px 60px rgba(0,0,0,0.6)" }}>
              <SplineCanvas />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════ FINAL CTA ════ */}
      <section style={{ padding: isMobile ? "64px 20px 100px" : "96px 24px 120px", background: isDark ? "#080810" : C.bgAlt, position: "relative", overflow: "hidden" }}>
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 600, borderRadius: "50%", background: `radial-gradient(circle,${C.accentGlow} 0%,transparent 70%)`, pointerEvents: "none" }}
        />
        <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center", position: "relative", zIndex: 1 }}>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <span className="section-number">Ready?</span>
            <h2 className="heading" style={{ fontSize: isMobile ? 38 : 72, fontWeight: 900, letterSpacing: -2, color: C.text, marginTop: 10, marginBottom: 14, lineHeight: 0.95 }}>
              Ready to <span className="grad-text">show up?</span>
            </h2>
            <p style={{ fontSize: isMobile ? 16 : 18, color: C.textSecondary, maxWidth: 440, margin: "0 auto 32px", lineHeight: 1.65 }}>
              Start with one session. Find your sport. Momentum follows.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", alignItems: "center" }}>
              <motion.button
                onClick={() => navigate("/sessions")}
                style={{ background: `linear-gradient(135deg,${C.accent},#0056CC)`, border: "none", color: "#fff", padding: isMobile ? "14px 24px" : "16px 34px", borderRadius: 14, cursor: "pointer", fontSize: isMobile ? 15 : 16, fontWeight: 800, fontFamily: "'Sora',sans-serif", display: "inline-flex", alignItems: "center", gap: 10, boxShadow: `0 8px 36px ${C.accentGlow}` }}
                whileHover={{ y: -4, boxShadow: `0 20px 60px ${C.accentGlow}`, filter: "brightness(1.1)" }}
                whileTap={{ scale: 0.97 }}
              >
                Join a session <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 14 }} />
              </motion.button>
              <motion.a href="https://apps.apple.com/ie/app/snows-proam/id6504996530" target="_blank" rel="noopener noreferrer" whileHover={{ y: -3, scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <img src={appstore} alt="App Store" style={{ height: isMobile ? 44 : 52, borderRadius: 10 }} />
              </motion.a>
              <motion.a href="https://play.google.com/store/apps/details?id=com.snowsproam.snowsproam" target="_blank" rel="noopener noreferrer" whileHover={{ y: -3, scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <img src={playstore} alt="Google Play" style={{ height: isMobile ? 44 : 52, borderRadius: 10 }} />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════ FOOTER ════ */}
      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: isMobile ? "40px 20px 100px" : "64px 24px 48px", background: C.footerBg }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1.6fr 1fr 1fr 1fr", gap: isMobile ? 28 : 24 }}>
          <div style={{ gridColumn: isMobile ? "1 / -1" : "auto" }}>
            <img src={logo} alt="Snows ProAm" style={{ height: 26, marginBottom: 12 }} />
            <p style={{ fontSize: 14, color: C.footerText, lineHeight: 1.65, maxWidth: 280, marginBottom: 18 }}>
              Find sessions, train consistently, grow through community. Where athletes belong.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <a href="https://apps.apple.com/ie/app/snows-proam/id6504996530" target="_blank" rel="noopener noreferrer">
                <img src={appstore} alt="iOS" style={{ height: 36, borderRadius: 8 }} />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.snowsproam.snowsproam" target="_blank" rel="noopener noreferrer">
                <img src={playstore} alt="Android" style={{ height: 36, borderRadius: 8 }} />
              </a>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: C.footerMuted, marginBottom: 12 }}>Product</div>
            {[
              { label: "Sessions", action: () => navigate("/sessions") },
              { label: "Leaderboard", action: () => navigate("/instituteleaderboard") },
              { label: "Live Matches", action: () => navigate("/livematches") },
              { label: "Steps Worldwide", href: "https://worldwidesteps.com/" },
              { label: "School Step Challenge", href: "https://worldwidesteps.com/schools" },
              { label: "Venues", action: () => navigate("/venues") },
              { label: "Wellness", action: () => navigate("/wellness") },
              ...(SHOW_EVENTS_PAGE ? [{ label: "Events", action: () => navigate("/events") }] : []),
              { label: "Coaching", action: () => navigate("/coaching") },
              { label: "Download", href: "https://linktr.ee/snowsproam" },
            ].map(l => (
              <div key={l.label} style={{ marginBottom: 8 }}>
                {l.href
                  ? <a href={l.href} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13.5, color: C.footerLink, fontWeight: 500 }}>{l.label}</a>
                  : <span onClick={l.action} style={{ fontSize: 13.5, color: C.footerLink, fontWeight: 500, cursor: "pointer" }}>{l.label}</span>
                }
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: C.footerMuted, marginBottom: 12 }}>Legal</div>
            {[{ label: "Terms of Service", href: "/terms" }, { label: "Privacy Policy", href: "/privacypolicy" }, { label: "Student Privacy", href: "/student-privacy" }, { label: "Copyright", href: "/copyrightpolicy" }, { label: "Support", href: "/support" }].map(l => (
              <div key={l.label} style={{ marginBottom: 8 }}>
                <a href={l.href} style={{ fontSize: 13.5, color: C.footerLink, fontWeight: 500 }}>{l.label}</a>
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: C.footerMuted, marginBottom: 12 }}>Social</div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              {[
                { icon: faFacebook, label: "Facebook", href: "https://facebook.com/snowsproam" },
                { icon: faTwitter, label: "X", href: "https://twitter.com/snowsproam" },
                { icon: faInstagram, label: "Instagram", href: "https://www.instagram.com/snowsproam/" },
                { icon: faTiktok, label: "TikTok", href: "https://www.tiktok.com/@snowsproam" },
              ].map(s => (
                <motion.a key={s.href} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={`Snows ProAm on ${s.label}`}
                  style={{ color: C.footerLink, fontSize: 13, minHeight: 44, display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 10px", border: `1px solid ${C.footerBorder}`, borderRadius: 10, textDecoration: "none" }}
                  whileHover={{ color: "#fff", y: -3 }}
                  transition={{ duration: 0.18 }}
                >
                  <FontAwesomeIcon icon={s.icon} style={{ fontSize: 18 }} aria-hidden="true" /><span>{s.label}</span>
                </motion.a>
              ))}
            </div>
          </div>
        </div>
        <div style={{ maxWidth: 1200, margin: "32px auto 0", borderTop: `1px solid ${C.footerBorder}`, paddingTop: 16, fontSize: 12.5, color: C.footerMuted, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <span>&copy; {new Date().getFullYear()} Snows ProAm. All rights reserved.</span>
          <a
            href="https://platform.fatsecret.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: C.footerLink, textDecoration: "underline" }}
          >
            Powered by fatsecret Platform API
          </a>
          <span>Made for athletes, by athletes.</span>
        </div>
      </footer>

      {/* ════ STICKY MOBILE CTA ════ */}
      <AnimatePresence>
        {!showMobileCtaDismissed && (
          <motion.div
            className="mobile-sticky-cta"
            style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.button
              onClick={() => navigate("/sessions")}
              style={{ flex: 1, background: C.accentGrad, border: "none", color: "#fff", padding: "13px", borderRadius: 12, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: "'Sora',sans-serif" }}
              whileTap={{ scale: 0.97 }}
            >
              Find a session
            </motion.button>
            <motion.a
              href="https://linktr.ee/snowsproam" target="_blank" rel="noopener noreferrer"
              style={{ flex: 1, background: isDark ? "rgba(255,255,255,0.07)" : "rgba(10,29,68,0.06)", border: `1px solid ${C.border}`, color: C.text, padding: "13px", borderRadius: 12, cursor: "pointer", fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans',sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}
              whileTap={{ scale: 0.97 }}
            >
              Download
            </motion.a>
            <motion.button
              onClick={() => setShowMobileCtaDismissed(true)}
              style={{ width: 40, height: 48, background: "transparent", border: "none", color: C.textMuted, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
              whileTap={{ scale: 0.9 }}
            >
              <FontAwesomeIcon icon={faXmark} style={{ fontSize: 14 }} />
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ════ MODAL ════ */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.70)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={e => { if (e.target === e.currentTarget) setIsModalOpen(false); }}
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{ width: "min(440px,100%)", maxHeight: "90svh", background: isDark ? "#12121F" : C.bgCard, borderRadius: 22, border: `1px solid ${C.border}`, boxShadow: isDark ? "0 40px 100px rgba(0,0,0,0.70)" : "0 40px 100px rgba(10,29,68,0.20)", overflow: "hidden", display: "flex", flexDirection: "column" }}
            >
              <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <img src={logo} alt="Snows ProAm" style={{ height: 22 }} />
                  <span className="heading" style={{ fontWeight: 800, fontSize: 15, color: C.text }}>Sign In</span>
                </div>
                <motion.button
                  onClick={() => setIsModalOpen(false)}
                  style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.textMuted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                  whileHover={{ background: C.bgAlt, rotate: 90 }}
                  whileTap={{ scale: 0.93 }}
                  transition={{ duration: 0.18 }}
                >
                  <FontAwesomeIcon icon={faXmark} style={{ fontSize: 13 }} />
                </motion.button>
              </div>
              <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                  {[{ label: "Sign In", active: true, action: () => { setIsSignUp(false); setError(""); } }].map(t => (
                    <motion.button
                      key={t.label}
                      onClick={t.action}
                      style={{ flex: 1, padding: "10px 14px", borderRadius: 10, cursor: "pointer", border: t.active ? `1px solid ${C.accent}` : `1px solid ${C.border}`, background: t.active ? C.accentBgLight : "transparent", color: t.active ? C.accent : C.textMuted, fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}
                      whileHover={{ borderColor: C.accent }}
                      whileTap={{ scale: 0.97 }}
                      transition={{ duration: 0.15 }}
                    >{t.label}</motion.button>
                  ))}
                </div>
                {isSignUp && !institutionPublicPricingHidden && (
                  <div style={{ padding: "10px 14px", borderRadius: 10, marginBottom: 16, background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, fontSize: 13, fontWeight: 600, color: C.textSecondary, textAlign: "center" }}>
                    All plans are currently <span style={{ color: C.accent }}>free to use</span> during launch.
                  </div>
                )}
                {!isSignUp ? (
                  <form onSubmit={handleSignIn} style={{ display: "grid", gap: 12 }}>
                    <InputField type="email" name="email" value={signInData.email} onChange={handleSignInChange} placeholder="Email" error={emailError} errorMsg="Email is required" autoComplete="email" C={C} />
                    <PasswordField value={signInData.password} name="password" onChange={handleSignInChange} placeholder="Password" show={showPassword} toggle={() => setShowPassword(v => !v)} error={passwordError} errorMsg="Password is required" autoComplete="current-password" C={C} />
                    {error && <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(255,77,106,0.08)", border: "1px solid rgba(255,77,106,0.20)", color: C.danger, fontSize: 13, fontWeight: 600 }}>{error}</motion.div>}
                    {unconfirmedSignIn && <button type="button" onClick={handleResendConfirmation} style={{ width: "100%", background: "transparent", border: `1px solid ${C.accentBorder}`, color: C.accent, padding: "12px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 800, fontFamily: "'Sora',sans-serif" }}>Resend confirmation email</button>}
                    <motion.button type="submit" style={{ width: "100%", background: C.accentGrad, border: "none", color: "#fff", padding: "13px", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: "'Sora',sans-serif" }} whileHover={{ y: -2, boxShadow: `0 8px 28px ${C.accentGlow}` }} whileTap={{ scale: 0.97 }}>Sign In</motion.button>
                    <div onClick={handleForgotPassword} style={{ textAlign: "center", fontSize: 13, color: C.accent, fontWeight: 600, cursor: "pointer", marginTop: 4 }}>Forgot Password?</div>
                  </form>
                ) : (
                  <form onSubmit={handleSignup} style={{ display: "grid", gap: 12 }}>
                    <InputField type="email" value={email} onChange={e => { setEmail(e.target.value); setEmailError(false); }} placeholder="Email" error={emailError} errorMsg="Required" autoComplete="email" C={C} />
                    <PasswordField value={password1} onChange={e => { setPassword1(e.target.value); setPassword1Error(false); }} placeholder="Password" show={showPassword} toggle={() => setShowPassword(v => !v)} error={password1Error} errorMsg="Required" autoComplete="new-password" C={C} />
                    <PasswordField value={password2} onChange={e => { setPassword2(e.target.value); setPassword2Error(false); }} placeholder="Confirm Password" show={showPassword} toggle={() => setShowPassword(v => !v)} error={password2Error} errorMsg="Required" autoComplete="new-password" C={C} />
                    {password1 && password2 && password1 !== password2 && <div style={{ fontSize: 12, color: C.danger, fontWeight: 600 }}>Passwords don't match</div>}
                    <InputField type="text" value={businessName} onChange={e => { setBusinessName(e.target.value); setBusinessNameError(false); }} placeholder="Institution Name" error={businessNameError} errorMsg="Required" C={C} />
                    <SelectField value={instituteType} onChange={e => { const v = e.target.value; setInstituteType(v); setInstituteTypeError(false); if (v === "School" || v === "College") { setGroupSize("fixed"); setGroupSizeError(false); } else { setGroupSize(""); } }} error={instituteTypeError} C={C} options={[{ v: "", l: "Select Institution Type" }, { v: "School", l: "School" }, { v: "College", l: "College / University" }, { v: "Club", l: "Sports Club" }, { v: "Gym", l: "Gym" }, { v: "Organisation", l: "Organisation" }, { v: "Company", l: "Company" }, { v: "Collective Group", l: "Collective Group" }]} />
                    {!institutionPublicPricingHidden && (instituteType === "School" || instituteType === "College") && (
                      <div style={{ padding: "10px 14px", borderRadius: 10, background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, fontSize: 13, fontWeight: 600, color: C.textSecondary }}>
                        {instituteType === "School" ? "Fixed: €299/year" : "Fixed: €599/year"}
                      </div>
                    )}
                    {["Club", "Organisation", "Company", "Collective Group"].includes(instituteType) && (
                      <>
                        <SelectField value={groupSize} onChange={e => { setGroupSize(e.target.value); setGroupSizeError(false); }} error={groupSizeError} C={C} options={[{ v: "", l: "Select organisation size" }, { v: "1-7", l: institutionPublicPricingHidden ? "1–7 members" : "1–7 members (€19/mo)" }, { v: "8-15", l: institutionPublicPricingHidden ? "8–15 members" : "8–15 (€29/mo)" }, { v: "16-30", l: institutionPublicPricingHidden ? "16–30 members" : "16–30 (€59/mo)" }, { v: "31-75", l: institutionPublicPricingHidden ? "31–75 members" : "31–75 (€99/mo)" }, { v: "76-150", l: institutionPublicPricingHidden ? "76–150 members" : "76–150 (€199/mo)" }, { v: "150+", l: "150+ (Contact)" }]} />
                        {groupSize && !institutionPublicPricingHidden && <div style={{ padding: "10px 14px", borderRadius: 10, background: C.accentBgSubtle, border: `1px solid ${C.accentBorder}`, fontSize: 13, fontWeight: 600, color: C.textSecondary }}>Plan: {groupSize === "1-7" ? "Starter (€19/mo)" : groupSize === "8-15" ? "Community (€29/mo)" : groupSize === "16-30" ? "Growth (€59/mo)" : groupSize === "31-75" ? "Organisation (€99/mo)" : groupSize === "76-150" ? "Business (€199/mo)" : "Enterprise (Contact)"}</div>}
                      </>
                    )}
                    <InputField type="text" value={address} onChange={e => { setAddress(e.target.value); setAddressError(false); }} placeholder="Institution Address" error={addressError} errorMsg="Required" autoComplete="street-address" C={C} />
                    <SelectField value={country} onChange={e => { setCountry(e.target.value); setRegion(""); setCountryError(false); }} error={countryError} C={C} options={[{ v: "", l: "Select Country" }, { v: "Ireland", l: "Ireland" }, { v: "United Kingdom", l: "United Kingdom" }]} />
                    <SelectField value={region} onChange={e => { setRegion(e.target.value); setRegionError(false); }} error={regionError} disabled={!country} C={C}
                      options={[{ v: "", l: country ? "Select Region" : "Pick country first" }, ...(country === "Ireland" ? ["Carlow", "Cavan", "Clare", "Cork", "Donegal", "Dublin", "Galway", "Kerry", "Kildare", "Kilkenny", "Laois", "Leitrim", "Limerick", "Longford", "Louth", "Mayo", "Meath", "Monaghan", "Offaly", "Roscommon", "Sligo", "Tipperary", "Waterford", "Westmeath", "Wexford", "Wicklow", "Antrim", "Armagh", "Derry", "Down", "Fermanagh", "Tyrone"] : country === "United Kingdom" ? ["Bedfordshire", "Berkshire", "Bristol", "Buckinghamshire", "Cambridgeshire", "Cheshire", "Cornwall", "Cumbria", "Derbyshire", "Devon", "Dorset", "Durham", "East Sussex", "Essex", "Gloucestershire", "Greater London", "Greater Manchester", "Hampshire", "Herefordshire", "Hertfordshire", "Isle of Wight", "Kent", "Lancashire", "Leicestershire", "Lincolnshire", "Merseyside", "Norfolk", "North Yorkshire", "Northamptonshire", "Northumberland", "Nottinghamshire", "Oxfordshire", "Rutland", "Shropshire", "Somerset", "South Yorkshire", "Staffordshire", "Suffolk", "Surrey", "Tyne and Wear", "Warwickshire", "West Midlands", "West Sussex", "West Yorkshire", "Wiltshire", "Worcestershire"] : []).map(r => ({ v: r, l: r }))]}
                    />
                    <label style={{ display: "flex", gap: 10, alignItems: "center", cursor: "pointer" }}>
                      <input type="checkbox" checked={agreeTerms} onChange={e => setAgreeTerms(e.target.checked)} style={{ width: 16, height: 16, accentColor: C.accent }} />
                      <span style={{ fontSize: 13, color: C.textSecondary }}>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: C.accent }}>Terms</a> and <a href="/privacypolicy" target="_blank" rel="noopener noreferrer" style={{ color: C.accent }}>Privacy Policy</a></span>
                    </label>
                    {error && <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(255,77,106,0.08)", border: "1px solid rgba(255,77,106,0.20)", color: C.danger, fontSize: 13, fontWeight: 600 }}>{error}</motion.div>}
                    <motion.button
                      type="submit"
                      disabled={password1 !== password2}
                      style={{ width: "100%", background: C.accentGrad, border: "none", color: "#fff", padding: "13px", borderRadius: 10, cursor: password1 !== password2 ? "not-allowed" : "pointer", fontSize: 14, fontWeight: 700, fontFamily: "'Sora',sans-serif", opacity: password1 !== password2 ? 0.5 : 1 }}
                      whileHover={password1 === password2 ? { y: -2, boxShadow: `0 8px 28px ${C.accentGlow}` } : {}}
                      whileTap={password1 === password2 ? { scale: 0.97 } : {}}
                    >
                      Create Account
                    </motion.button>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ─── FORM SUB-COMPONENTS ─── */
const inputBase = (error, C) => ({
  width: "100%", padding: "12px 14px", borderRadius: 10,
  border: `1px solid ${error ? "rgba(255,77,106,0.45)" : C.inputBorder}`,
  background: C.inputBg, color: C.inputText, fontSize: 14,
  fontFamily: "'DM Sans',sans-serif", outline: "none",
  transition: "border-color 0.15s,box-shadow 0.15s",
  boxShadow: error ? "0 0 0 3px rgba(255,77,106,0.10)" : "none",
});

const InputField = ({ type = "text", value, onChange, placeholder, error, errorMsg, autoComplete, name, C }) => (
  <div>
    <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} autoComplete={autoComplete}
      style={inputBase(error, C)}
      onFocus={e => { if (!error) { e.currentTarget.style.borderColor = "rgba(43,127,255,0.55)"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(43,127,255,0.10)"; } }}
      onBlur={e => { if (!error) { e.currentTarget.style.borderColor = C.inputBorder; e.currentTarget.style.boxShadow = "none"; } }}
    />
    {error && <div style={{ fontSize: 12, color: "#FF4D6A", fontWeight: 600, marginTop: 4 }}>{errorMsg}</div>}
  </div>
);

const PasswordField = ({ value, onChange, placeholder, show, toggle, error, errorMsg, autoComplete, name, C }) => (
  <div>
    <div style={{ position: "relative" }}>
      <input type={show ? "text" : "password"} name={name} value={value} onChange={onChange} placeholder={placeholder} autoComplete={autoComplete}
        style={{ ...inputBase(error, C), paddingRight: 42 }}
        onFocus={e => { if (!error) { e.currentTarget.style.borderColor = "rgba(43,127,255,0.55)"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(43,127,255,0.10)"; } }}
        onBlur={e => { if (!error) { e.currentTarget.style.borderColor = C.inputBorder; e.currentTarget.style.boxShadow = "none"; } }}
      />
      <FontAwesomeIcon icon={show ? faEyeSlash : faEye} onClick={toggle}
        style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: C.textMuted, fontSize: 14 }} />
    </div>
    {error && <div style={{ fontSize: 12, color: "#FF4D6A", fontWeight: 600, marginTop: 4 }}>{errorMsg}</div>}
  </div>
);

const SelectField = ({ value, onChange, error, disabled, options, C }) => (
  <div>
    <select value={value} onChange={onChange} disabled={disabled} style={{ ...inputBase(error, C), cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, appearance: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='${encodeURIComponent(C.mode === "dark" ? "rgba(240,240,255,0.4)" : "rgba(10,29,68,0.35)")}' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center", backgroundSize: "12px", paddingRight: 36 }}>
      {options.map(o => (
        <option key={o.v} value={o.v} style={{ background: C.mode === "dark" ? "#12121F" : "#FFFFFF", color: C.mode === "dark" ? "#F0F0FF" : "#0A0E1F" }}>{o.l}</option>
      ))}
    </select>
    {error && <div style={{ fontSize: 12, color: "#FF4D6A", fontWeight: 600, marginTop: 4 }}>Required</div>}
  </div>
);

export default LaunchPage;
